/**
 * As palavras: o dicionário e as duas contas que ele precisa.
 *
 * A chave de cada verbete é a própria frase em português. Não é preguiça de
 * inventar nomes -- é o que faz uma tela continuar legível depois de traduzida:
 *
 *   <h1>{texto('Bom dia')}</h1>        em vez de   <h1>{texto('painel.saudacao')}</h1>
 *
 * e é o que faz uma frase ainda não traduzida sair em português em vez de sair
 * como `painel.saudacao` na cara de quem usa. O app nasceu em português, o
 * português é o original, e o dicionário só tem um lado: o inglês.
 *
 * O preço é um só, e está anotado aqui para ninguém tropeçar nele depois:
 * mudar a frase em português quebra o laço com a tradução, e a tela volta a
 * falar português para quem pediu inglês. Quem mexer numa frase daqui mexe nas
 * duas pontas.
 *
 * Este arquivo não importa nada de propósito. É assim que `test/idioma.mjs`
 * consegue carregá-lo direto no Node, que só apaga `import type` e não sabe
 * resolver um caminho sem extensão -- o mesmo motivo de `musica/casar.ts`
 * existir separado. O estado, o disco e o React moram em `idioma.ts`.
 */

export type Idioma = 'pt' | 'en';

/** O nome de cada idioma na própria língua, para o botão dos ajustes. */
export const NOMES_DE_IDIOMA: Record<Idioma, string> = { pt: 'Português', en: 'English' };

/**
 * A etiqueta que o `Intl` entende. O português daqui é o do Brasil, e o inglês
 * é o dos Estados Unidos: são os dois que decidem ordem de data, separador de
 * milhar e o nome dos meses.
 */
export const LOCALES: Record<Idioma, string> = { pt: 'pt-BR', en: 'en-US' };

export type Valores = Record<string, string | number>;

/**
 * Troca cada `{nome}` pelo valor. Existe para a frase traduzida poder mudar a
 * ordem das partes -- "3 receitas de {pessoa}" e "{pessoa}'s 3 recipes" dizem o
 * mesmo com as peças em lugares diferentes, e emendar pedaços com `+` no meio
 * do JSX impediria isso.
 *
 * Um `{nome}` sem valor fica como está. É de propósito: some uma palavra da
 * frase, mas não some a frase inteira, e o teste de `test/idioma.mjs` pega o
 * descuido antes de ele chegar na tela.
 */
export function preencher(molde: string, valores?: Valores): string {
  if (!valores) return molde;
  return molde.replace(/\{(\w+)\}/g, (inteiro, nome: string) => {
    const v = valores[nome];
    return v === undefined ? inteiro : String(v);
  });
}

/**
 * A frase no idioma pedido. Em português é a própria chave -- não há dicionário
 * de ida e volta para manter.
 */
export function traduzirPara(idioma: Idioma, fonte: string, valores?: Valores): string {
  return preencher(idioma === 'pt' ? fonte : (EN[fonte] ?? fonte), valores);
}

/**
 * O dicionário.
 *
 * Cresce por ondas, tela a tela. Uma frase que ainda não está aqui sai em
 * português, e isso é o pior que pode acontecer -- nunca uma tela vazia nem uma
 * chave crua.
 *
 * A ordem é a das telas, não a do alfabeto: quem traduz uma tela lê o bloco
 * dela inteiro e vê as frases vizinhas, que é onde o tom se decide.
 */
export const EN: Record<string, string> = {
  // -- A casa: as abas de baixo, a barra de cima, a porta.
  //
  // "Nosso Sempre", "Cinema" e "Kovu" ficam como estão de propósito: o primeiro
  // é o nome do app, o segundo se escreve igual nas duas línguas e o terceiro é
  // o nome do bichinho. Estão aqui escritos mesmo assim para quem ler o bloco
  // não achar que foram esquecidos.
  'Nosso Sempre': 'Nosso Sempre',
  Seções: 'Sections',
  Casa: 'Home',
  Painel: 'Dashboard',
  Cinema: 'Cinema',
  Lembranças: 'Memories',
  Desejos: 'Wishes',
  Livros: 'Books',
  Ouvir: 'Listen',
  Kovu: 'Kovu',
  Mais: 'More',
  Início: 'Home',
  Voltar: 'Back',
  Fechar: 'Close',
  Abrindo: 'Opening',
  'Abrindo...': 'Opening...',
  Carregando: 'Loading',
  'Carregando...': 'Loading...',
  Salvar: 'Save',
  Salvando: 'Saving',
  Salvo: 'Saved',
  Fixar: 'Pin',
  Desafixar: 'Unpin',
  Detalhes: 'Details',
  Cancelar: 'Cancel',
  Apagar: 'Delete',
  Editar: 'Edit',
  Adicionar: 'Add',
  Confirmar: 'Confirm',
  Pronto: 'Done',
  Tentar: 'Try',
  'Tentar de novo': 'Try again',
  Buscar: 'Search',
  'Buscando...': 'Searching...',
  Procurar: 'Search',
  Limpar: 'Clear',
  Ajustes: 'Settings',
  Sair: 'Sign out',
  Entrar: 'Sign in',
  Hoje: 'Today',
  Ontem: 'Yesterday',
  Amanhã: 'Tomorrow',
  Sim: 'Yes',
  Não: 'No',
  'Sem conexão ao vivo. O que aparece aqui pode estar atrasado.':
    'No live connection. What you see here may be out of date.',
  // O recado da barreira, quando uma tela cai. Fica entre os de toda a casa
  // porque é de toda a casa: qualquer uma das setenta pode chegar aqui.
  'Esta tela parou. O resto da casa continua de pé.':
    'This screen stopped. The rest of the home is still standing.',

  // -- A porta de senha.
  //
  // É a primeira tela, e a única que aparece antes de qualquer resposta do
  // servidor -- por isso a escolha de idioma mora no aparelho e não na conta.
  'Nossa casa': 'Our home',
  Senha: 'Passcode',
  'Senha errada': 'Wrong passcode',
  'Senha errada. Tente de novo.': 'Wrong passcode. Try again.',
  Trancar: 'Lock',
  'Trancar este aparelho': 'Lock this device',
  'Este aparelho está trancado': 'This device is locked',
  Código: 'Code',
  'Primeiro acesso': 'First time here',
  'Como você quer aparecer nesta casa?': 'How do you want to appear in this home?',
  'Quem está entrando?': 'Who is coming in?',
  'Seu nome': 'Your name',
  'Depois dá para mudar tudo em Ajustes.': 'You can change all of this later in Settings.',
  'Este lugar ({n})': 'This spot ({n})',
  'Abrir nossa casa': 'Open our home',
  'Tenho um código de casa': 'I have a home code',
  'Já tenho meu código': 'I already have my code',
  'Escolha um código de 4 dígitos, {quem}. Ele vai ser o seu para sempre.':
    'Pick a 4-digit code, {quem}. It will be yours forever.',
  'Digite o mesmo código de novo para confirmar.': 'Type the same code again to confirm.',
  'Esse código é fácil demais de adivinhar. Escolha outro.':
    'That code is too easy to guess. Pick another one.',
  'Os códigos não bateram. Vamos escolher de novo.': "The codes didn't match. Let's pick again.",
  'Não deu para salvar o código. Tente de novo.': 'Could not save the code. Try again.',
  'Algo deu errado. Tente de novo.': 'Something went wrong. Try again.',
  '{feitos} de {total} dígitos': '{feitos} of {total} digits',
  'Entrar com chave de acesso': 'Sign in with a passkey',
  'Não foi possível usar sua chave.': 'Could not use your passkey.',
  'Essa chave não abre esta casa.': "That passkey doesn't open this home.",
  'Essa senha não abre.': "That passcode doesn't open it.",
  'A senha tem quatro números.': 'The passcode is four digits.',
  'Muitas tentativas. Tente de novo em {minutos} min.':
    'Too many attempts. Try again in {minutos} min.',
  'Não deu para falar com o servidor.': 'Could not reach the server.',
  'Sem conexão com o servidor.': 'No connection to the server.',
  'Não foi possível falar com o servidor. Verifique a conexão e toque para tentar de novo.':
    'Could not reach the server. Check your connection and tap to try again.',
  'O app ainda não está conectado ao banco de dados. Toque para tentar de novo.':
    'The app is not connected to the database yet. Tap to try again.',
  'Nossa casa está aberta': 'Our home is open',
  'Guarde este código:': 'Keep this code:',
  'É com ele que a outra pessoa entra na mesma casa, do aparelho dela.':
    'It is what lets the other person join the same home, from their device.',
  Entendi: 'Got it',
  'Código da casa': 'Home code',
  'O que apareceu no outro aparelho': 'What showed up on the other device',
  'Não deu para abrir a casa.': 'Could not open the home.',
  'Não deu para entrar na casa.': 'Could not join the home.',

  // -- Os ajustes: a troca de idioma.
  Idioma: 'Language',
  'a língua em que o app fala com você': 'the language the app speaks to you in',
  Automático: 'Automatic',
  'Segue o aparelho': 'Follows the device',
  '"Automático" segue a língua deste aparelho.': '"Automatic" follows this device\'s language.',

  // -- O painel: o contador de aniversário.
  //
  // As seis medidas e as palavras da sobrancelha estão em minúsculas de
  // propósito. Quem as desenha é que decide a caixa -- o site com um
  // `text-transform: uppercase` no CSS, o celular com um `.toUpperCase()` --,
  // então um verbete só serve aos dois lugares em vez de dois quase iguais.
  'Tempo juntos': 'Time together',
  ano: 'year',
  anos: 'years',
  mês: 'month',
  meses: 'months',
  dias: 'days',
  horas: 'hours',
  minutos: 'minutes',
  segundos: 'seconds',
  '{dias} dias de nós · desde {data}': '{dias} days of us · since {data}',

  // -- O tocador de música e a vitrola.
  Tocar: 'Play',
  Pausar: 'Pause',
  Parar: 'Stop',
  'Fechar o tocador': 'Close the player',
  'Voltar 15 segundos': 'Back 15 seconds',
  'Adiantar 15 segundos': 'Forward 15 seconds',
  // Os mesmos três com a tecla junto: são o `title` do computador, onde os
  // atalhos existem. O nome que o leitor de tela anuncia continua sendo o de
  // cima, sem a tecla -- ouvir "(espaço)" a cada foco não ajuda ninguém.
  'Tocar (espaço)': 'Play (space)',
  'Pausar (espaço)': 'Pause (space)',
  'Voltar 15 segundos (←)': 'Back 15 seconds (←)',
  'Adiantar 15 segundos (→)': 'Forward 15 seconds (→)',
  // Só aparecem quando o item do acervo tem mais de uma faixa: um show do
  // `etree`, uma coletânea de netlabel. Num 78 de faixa única, não há próxima.
  'Faixa anterior': 'Previous track',
  'Próxima faixa': 'Next track',
  // A folha com o repertório inteiro, e o "3 de 20" que a abre. O `{n} de
  // {total}` já está lá embaixo, no bloco dos números -- é o mesmo de sempre.
  'As faixas': 'The tracks',
  'Ver as faixas': 'See the tracks',
  'Ouvir aqui': 'Listen here',
  'Ouvir no YouTube Music': 'Listen on YouTube Music',
  'Procurando uma gravação livre...': 'Looking for a free recording...',
  'Gravação livre do Internet Archive: {album}':
    'Free recording from the Internet Archive: {album}',
  'Nada tocando agora': 'Nothing playing right now',
  'Ouvindo agora': 'Listening now',

  // -- A cozinha: as palavras que a `contagem()` recebe.
  //
  // Entram soltas, em singular e plural, porque quem escolhe entre as duas é a
  // `contagem()` em `cozinha/receita.ts`, antes de traduzir. O resto das telas
  // da cozinha vem numa onda própria.
  ingrediente: 'ingredient',
  ingredientes: 'ingredients',
  'item em casa': 'item at home',
  'itens em casa': 'items at home',
  'item guardado': 'item stored',
  'itens guardados': 'items stored',

  // -- As peças que aparecem em várias telas: a barra de pulo, os cartões do
  // painel, o visor de fotos, as folhas que sobem de baixo.
  //
  // Frases que já nascem em inglês na tela -- "Living Archive", "Digital
  // Roulette", "Search stars…", "You should..." -- não têm verbete nenhum aqui,
  // e é de propósito: a chave é a própria frase, então elas já saem certas.
  'ARQUIVO VIVO': 'LIVING ARCHIVE',
  'Não deu para carregar as memórias': 'Could not load the memories',
  'Carregando as memórias...': 'Loading the memories...',
  'Marcos guardados': 'Milestones kept',
  // A folha de guardar um marco, que o Flutter não tem. Os cinco nomes de
  // categoria -- "First Date", "Trip", "Achievement", "Anniversary", "Memory" --
  // não entram aqui pelo mesmo motivo do bloco acima: já nascem em inglês.
  'Guardar um marco': 'Keep a milestone',
  'Nenhum marco ainda': 'No milestone yet',
  'O primeiro capítulo de vocês começa aqui.': 'Your first chapter starts here.',
  'O nome do marco': 'The name of the milestone',
  'Conte o que aconteceu': 'Tell what happened',
  'Tipo de marco': 'Milestone type',
  'Tirar esta foto': 'Remove this photo',
  'Não deu para guardar o marco: {message}': 'Could not keep the milestone: {message}',
  'IR PARA': 'JUMP TO',
  'Ir para uma seção': 'Jump to a section',
  'Progresso do nível': 'Level progress',
  'Subiu de nível!': 'Level up!',
  'Tocando agora': 'Playing now',
  'Onde a faixa está': 'Track position',
  Volume: 'Volume',
  /* O par sem a tecla já existe lá embaixo, com o cinema: é a mesma ação e a
     mesma frase, e são estas duas que o balão do computador acrescenta. */
  'Tirar o som (M)': 'Mute (M)',
  'Ligar o som (M)': 'Turn the sound on (M)',
  'Abrindo a gravação': 'Opening the recording',
  'Assistir Junto': 'Watch Together',
  NOVO: 'NEW',
  'CINEMA EM TEMPO REAL': 'REAL-TIME CINEMA',
  'Abrir o Assistir Junto': 'Open Watch Together',
  'Sessão de cinema em tempo real, dá play junto.':
    'A real-time cinema session, press play together.',
  'TOQUE PARA ENTRAR NA SALA': 'TAP TO JOIN THE ROOM',
  'GIRE PARA SORTEAR O ENCONTRO': 'SPIN TO DRAW YOUR DATE',
  'Sortear um encontro': 'Draw a date',
  'Perfeito!': 'Perfect!',
  'Trocar a planta': 'Change the plant',
  'Nosso jardim': 'Our garden',
  'Como está seu coração hoje?': 'How is your heart today?',
  'Estatísticas de música': 'Music stats',
  'Nenhuma lista da Alexa foi importada ainda. Isso se faz na tela da Alexa.':
    'No Alexa list has been imported yet. You do that on the Alexa screen.',
  'Abrir a tela da Alexa': 'Open the Alexa screen',
  'Procurar de novo': 'Search again',
  Mandando: 'Sending',
  'Trazer para a lista de compras': 'Bring to the shopping list',
  'Mandar o que falta comprar': 'Send what is still to buy',
  'Toque para escolher uma foto': 'Tap to pick a photo',
  Legenda: 'Caption',
  'Escreva uma legenda…': 'Write a caption…',
  'A foto escolhida': 'The chosen photo',
  'Não deu para guardar a foto agora.': 'Could not save the photo right now.',
  'Um instante': 'One moment',
  'Ninguém vinculou o Last.fm ainda. Isso se faz em Ajustes, em Música.':
    'Nobody has linked Last.fm yet. You do that in Settings, under Music.',
  'Toque para ouvir junto →': 'Tap to listen along →',
  'Abrir a conversa com o Kovu': 'Open the chat with Kovu',
  'Segure para fazer carinho': 'Hold to pet',
  'Falar com o Kovu': 'Talk to Kovu',
  'Planeje o próximo dia especial': 'Plan the next special day',
  'Coloque a primeira memória no álbum': 'Put the first memory in the album',
  'Nenhuma carta ainda. Continue voltando aqui!': 'No letters yet. Keep coming back!',
  'Toque para marcar a próxima data especial': 'Tap to mark the next special date',
  'Ver todas as lembranças': 'See all the memories',
  'Memória deste dia. Toque para ver.': 'A memory from this day. Tap to see it.',
  'Neste dia': 'On this day',
  'Procurar nas estrelas': 'Search the stars',
  'Pote de vidro com os bilhetes de gratidão. Toque para sortear um.':
    'A glass jar with the gratitude notes. Tap to draw one.',
  'Sortear uma estrela com festa': 'Draw a star with confetti',
  'Guardar uma estrela': 'Keep a star',
  'Limpar a busca': 'Clear the search',
  'Filtrar por categoria': 'Filter by category',
  Tudo: 'All',
  'Sortear com festa': 'Draw with confetti',
  'Guardar a etiqueta': 'Save the tag',
  'Lista de sonhos vazia. Toque para abrir.': 'The dream list is empty. Tap to open it.',
  'A lista de sonhos está vazia. Comecem pelo primeiro!':
    'The dream list is empty. Start with the first one!',
  'Nossa lista de sonhos': 'Our dream list',
  'Nenhum livro ainda. Ache a próxima leitura!': 'No books yet. Find your next read!',
  'Ver a estante inteira': 'See the whole shelf',
  'Fechar a foto': 'Close the photo',
  'Apagar a foto': 'Delete the photo',
  'Toque para ver de perto': 'Tap to zoom in',
  'Toque para caber na tela': 'Tap to fit the screen',
  Foto: 'Photo',
  'Foto anterior': 'Previous photo',
  'Próxima foto': 'Next photo',

  // -- O painel e a casa: a primeira tela depois da porta.
  'Como estamos hoje': 'How we are today',
  'ainda não disse': 'has not said yet',
  'ACESSO RÁPIDO': 'QUICK ACCESS',
  'Entrar na Academia Nosso Sempre': 'Enter the Nosso Sempre Academy',
  'Entrar na Zona de Jogos': 'Enter the Game Zone',
  'Uma casa é o lugar de vocês aqui dentro. Uma pessoa abre, guarda o código e passa para a outra.':
    'A home is the place that belongs to the two of you in here. One person opens it, keeps the code and passes it to the other.',
  'Abrir a nossa casa': 'Open our home',
  'Já temos uma casa': 'We already have a home',
  'Voltar para a porta': 'Back to the door',

  // -- A cozinha: a despensa, as compras, o cardápio e o livro de receitas.
  Despensa: 'Pantry',
  'A despensa está vazia': 'The pantry is empty',
  'Escreva aqui embaixo o que tem em casa. Depois da compra, o botão de guardar na lista traz tudo de uma vez.':
    'Write down here what you have at home. After shopping, the store button on the list brings everything in at once.',
  'DÁ PRA FAZER': 'YOU CAN MAKE',
  '1 litro de leite, arroz...': '1 liter of milk, rice...',
  'Guardar na despensa': 'Store in the pantry',
  Validade: 'Best before',
  'Tirar da despensa': 'Take out of the pantry',
  Gerando: 'Generating',
  'Gerar a partir do cardápio': 'Generate from the menu',
  'Guardar o carrinho na despensa': 'Store the cart in the pantry',
  'Lista de compras': 'Shopping list',
  'A lista está vazia': 'The list is empty',
  'Monte o cardápio da semana e a lista sai dele. Dá para digitar o que faltar aqui embaixo.':
    'Build the menu for the week and the list comes out of it. You can type whatever is missing down here.',
  'Gerar do cardápio': 'Generate from the menu',
  '2 kg de arroz, sabão em pó...': '2 kg of rice, laundry soap...',
  'Colocar na lista': 'Put on the list',
  'Tirar da lista': 'Take off the list',
  'Lista de compras da semana': "The week's shopping list",
  'Cardápio da semana': 'Menu for the week',
  'Semana anterior': 'Previous week',
  'Próxima semana': 'Next week',
  'Menos uma porção': 'One serving less',
  'Mais uma porção': 'One serving more',
  'Procurar no livro': 'Search the book',
  Semana: 'Week',
  'Tirar do cardápio': 'Remove from the menu',
  Receita: 'Recipe',
  Corrigir: 'Fix',
  PORÇÕES: 'SERVINGS',
  'Ninguém escreveu o que ela leva.': 'Nobody wrote down what goes in it.',
  'Nova receita': 'New recipe',
  'Importar de um link': 'Import from a link',
  'Livro de receitas': 'Recipe book',
  'Buscar por nome ou ingrediente': 'Search by name or ingredient',
  Endereço: 'Address',
  'Só favoritas': 'Favorites only',
  Nome: 'Name',
  'Estrogonofe de frango': 'Chicken stroganoff',
  Descrição: 'Description',
  'O que tem de bom nessa': 'What is good about this one',
  Porções: 'Servings',
  Minutos: 'Minutes',
  Refeição: 'Meal',
  Ingredientes: 'Ingredients',
  'Novo ingrediente': 'New ingredient',
  '2 xíc farinha de trigo': '2 cups all-purpose flour',
  'Adicionar ingrediente': 'Add ingredient',
  'Modo de preparo': 'Steps',
  'Um passo por linha': 'One step per line',
  Remover: 'Remove',
  'Corredor no mercado': 'Aisle at the market',

  // -- A Alexa: as listas que vêm de fora.
  'Listas da Alexa': 'Alexa lists',
  'Nenhuma lista importada ainda. Conecte a Alexa aqui embaixo e escolha o que vocês dois querem ver.':
    'No list imported yet. Connect Alexa below and pick what the two of you want to see.',
  'COMPARTILHADAS COM VOCÊS': 'SHARED WITH YOU BOTH',
  'A sessão está guardada só no servidor. Ela nunca volta para o app, para nenhum de vocês dois. O servidor renova ela sozinho a cada poucas horas e já traz as listas atualizadas na mesma ida, então não precisa colar de novo nem apertar importar toda hora.':
    'The session is kept on the server only. It never comes back to the app, for either of you. The server renews it by itself every few hours and brings the updated lists in the same trip, so there is no need to paste again or hit import all the time.',
  'Salvar os cookies novos': 'Save the new cookies',
  'Cancelar a troca': 'Cancel the change',
  'Trocar os cookies': 'Change the cookies',
  'Desconectar a Alexa': 'Disconnect Alexa',
  'Conectar a Alexa': 'Connect Alexa',
  'Nenhuma lista encontrada na conta.': 'No list found in the account.',
  'Serve também o cookies.txt de uma extensão de exportar cookies, ou o JSON dela. Só pelo console não dá: o cookie de login não aparece lá, e a conexão falha na hora de mandar a compra.':
    'The cookies.txt from a cookie-export extension works too, or its JSON. The console alone will not do: the login cookie does not show up there, and the connection fails when it is time to send the shopping.',
  'Cookies da sessão da Alexa': 'Alexa session cookies',
  'Tirar daqui': 'Remove from here',
  'Tudo riscado.': 'All crossed off.',
  'Compartilhadas com vocês': 'Shared with you both',
  Tirando: 'Removing',
  Riscando: 'Crossing off',

  // -- Os livros: a estante da casa, o acervo de fora e o leitor.
  'Adicionar ao Nosso Sempre?': 'Add to Nosso Sempre?',
  Meus: 'Mine',
  Nossos: 'Ours',
  'Para ler': 'To read',
  Lido: 'Read',
  'Ache o próximo livro': 'Find the next book',
  'Busque por título, autor ou assunto...': 'Search by title, author or subject...',
  'Busque por título, autor ou assunto': 'Search by title, author or subject',
  'Onde guardar': 'Where to keep it',
  'Como guardar': 'How to keep it',
  'SOBRE O LIVRO': 'ABOUT THE BOOK',
  Compartilhar: 'Share',
  Ler: 'Read',
  Baixar: 'Download',
  'Buscando detalhes': 'Fetching details',
  'Só domínio público. De graça para ler, baixar e compartilhar.':
    'Public domain only. Free to read, download and share.',
  'Buscando parecidos': 'Fetching similar ones',
  'Procurando os arquivos': 'Looking for the files',
  'Sobre o livro': 'About the book',
  Capítulos: 'Chapters',
  'Abrindo as páginas': 'Opening the pages',
  'Abrindo as páginas...': 'Opening the pages...',
  'Este título só abre algumas páginas aqui. As outras pedem empréstimo no Internet Archive.':
    'This title only opens a few pages here. The rest have to be borrowed at the Internet Archive.',
  Anterior: 'Previous',
  Próxima: 'Next',
  'Capítulo anterior': 'Previous chapter',
  'Próximo capítulo': 'Next chapter',
  'Ajustes de leitura': 'Reading settings',
  'Tamanho da letra': 'Text size',
  Tema: 'Theme',
  'Formatos de domínio público': 'Public-domain formats',
  'Nenhum arquivo disponível para baixar.': 'No file available to download.',
  'Velocidade da leitura': 'Reading speed',
  'NOSSOS LIVROS': 'OUR BOOKS',
  'Abrir Nossos Livros': 'Open Our Books',
  'Buscar no acervo inteiro': 'Search the whole collection',
  'Buscar no acervo inteiro...': 'Search the whole collection...',
  'ACERVO COMPLETO': 'FULL COLLECTION',
  'ACHE A PRÓXIMA OBSESSÃO': 'FIND YOUR NEXT OBSESSION',
  'Buscar livros': 'Search books',
  'Ordem dos resultados': 'Result order',
  'AUDIOLIVROS NARRADOS, DO LIBRIVOX': 'NARRATED AUDIOBOOKS, FROM LIBRIVOX',
  'Buscar audiolivros': 'Search audiobooks',
  'Toque para explorar': 'Tap to explore',
  'Título, autor, assunto...': 'Title, author, subject...',
  'Título, autor...': 'Title, author...',
  'Adicionar um livro': 'Add a book',
  'Para ler juntos': 'To read together',
  Lidos: 'Read',
  'Nossos livros': 'Our books',
  'Este espaço é só de quem mora nesta casa.':
    'This space belongs only to the people who live in this home.',
  'Eu li': 'I read it',
  'Tirar de Nossos Livros': 'Remove from Our Books',
  'Leram juntos': 'Read together',
  'Adicione um livro para ler junto': 'Add a book to read together',
  'Tirar de Nossos Livros?': 'Remove from Our Books?',
  Tirar: 'Remove',
  Audiolivro: 'Audiobook',
  'Posição na faixa': 'Track position',
  'Voltar 30 segundos': 'Back 30 seconds',
  'Avançar 30 segundos': 'Forward 30 seconds',
  CAPÍTULOS: 'CHAPTERS',
  'Seções da biblioteca': 'Library sections',

  // -- O cinema: a busca, o título, a sessão a dois e a TV ao vivo.
  'Títulos, atores, gêneros': 'Titles, actors, genres',
  'Buscar no cinema': 'Search the cinema',
  'Só filmes': 'Movies only',
  'Só séries': 'Series only',
  'Buscas populares': 'Popular searches',
  'Populares agora': 'Popular now',
  'Achar algo': 'Find something',
  'Nada encontrado': 'Nothing found',
  'Tente outro título ou palavra.': 'Try another title or word.',
  'Nenhum título com esse filtro.': 'No title with that filter.',
  'Filmes e séries que vocês salvarem ou assistirem aparecem aqui.':
    'Movies and series the two of you save or watch show up here.',
  'Minha Lista': 'My List',
  'Nada aqui ainda.': 'Nothing here yet.',
  'Downloads da sessão': 'Session downloads',
  'Buscar filmes públicos': 'Search public-domain movies',
  'Escolher arquivo': 'Choose file',
  'Voltar aos resultados': 'Back to the results',
  Buscando: 'Searching',
  'Filmes de domínio público, prontos para a sessão. Ou busque outro título ali em cima.':
    'Public-domain movies, ready for the session. Or search another title up there.',
  'Sem dados de temporada': 'No season data',
  'Sem dados de episódio': 'No episode data',
  'Pergunte ao Kovu': 'Ask Kovu',
  'Kovu está pensando...': 'Kovu is thinking...',
  'Definir como fonte padrão': 'Set as the default source',
  'Nenhuma versão encontrada para este título. Use um dos servidores da lista.':
    'No version found for this title. Use one of the servers in the list.',
  'TV ao vivo': 'Live TV',
  'Abrir a página de aprovação': 'Open the approval page',
  'Esperando a aprovação...': 'Waiting for approval...',
  'Conectar agora': 'Connect now',
  'Procurar canal, filme ou série': 'Search channel, movie or series',
  Filtrar: 'Filter',
  'Nada com esse nome.': 'Nothing by that name.',
  'AO VIVO': 'LIVE',
  'Assistir junto': 'Watch together',
  'SÓ DO CASAL': 'JUST THE TWO OF YOU',
  'Sua biblioteca do Jellyfin': 'Your Jellyfin library',
  'Escolha um título': 'Pick a title',
  'Sua lista está vazia': 'Your list is empty',
  'Adicione filmes no Cinema ou na aba Minha lista e volte para começar aqui.':
    'Add movies in Cinema or in the My list tab and come back to start here.',
  'Abrir Minha lista': 'Open My list',
  'ASSISTIR JUNTO': 'WATCH TOGETHER',
  'Servidor: Jellyfin próprio': 'Server: your own Jellyfin',
  'Stream HLS · play/pause/avanço sincronizados, sem anúncio':
    'HLS stream · synced play/pause/seek, no ads',
  'Nenhuma sessão rolando': 'No session running',
  'Toque em um pôster abaixo para começar uma sessão sincronizada.':
    'Tap a poster below to start a synced session.',
  'SESSÃO DE CINEMA': 'CINEMA SESSION',
  'Nenhum filme por aqui ainda': 'No movie around here yet',
  'Buscar na sua pasta': 'Search your folder',
  'Busque filmes do Jellyfin': 'Search Jellyfin movies',
  'Busque filmes do Jellyfin por título ou nome do arquivo':
    'Search Jellyfin movies by title or file name',
  'Chat temporário': 'Temporary chat',
  'Limpar o chat': 'Clear the chat',
  'Buscar filmes do Jellyfin': 'Search Jellyfin movies',
  'Guardado nesta sala': 'Kept in this room',
  'Fechar o chat': 'Close the chat',
  'O chat está vazio': 'The chat is empty',
  'Manda um oi para animar': 'Say hi to get it going',
  'Escreva uma mensagem…': 'Write a message…',
  Conta: 'Account',
  'Continuar assistindo': 'Keep watching',
  Série: 'Series',
  'Mais info': 'More info',
  Avançar: 'Forward',
  'Servidor de vídeo': 'Video server',
  'Ache um filme para apresentar': 'Find a movie to show',
  'URL do stream': 'Stream URL',
  'URL da legenda (opcional)': 'Subtitle URL (optional)',
  'Passar pelo proxy do Nosso Sempre': 'Go through the Nosso Sempre proxy',
  'Passar pelo proxy do Nosso Sempre (necessário em servidores sem CORS)':
    'Go through the Nosso Sempre proxy (needed on servers without CORS)',
  'Conectar e usar este servidor': 'Connect and use this server',
  'Jellyfin de casa': 'Home Jellyfin',
  'Abrindo a sessão...': 'Opening the session...',
  'Tentar outra fonte': 'Try another source',
  Encerrar: 'End',
  'Encerrar a sessão?': 'End the session?',
  'Quem está com você volta para o cinema.': 'Whoever is with you goes back to the cinema.',
  'Buscando o filme': 'Fetching the movie',
  'Testando os servidores...': 'Testing the servers...',
  'Este servidor não conseguiu iniciar o vídeo.': 'This server could not start the video.',
  'A fonte respondeu 404 ou não respondeu. Tente outra opção abaixo.':
    'The source answered 404 or did not answer. Try another option below.',
  'TENTAR OUTRA FONTE': 'TRY ANOTHER SOURCE',
  'Abrir no navegador': 'Open in the browser',
  Você: 'You',
  'Sem chamada': 'No call',
  Desligar: 'Hang up',
  'Efeitos da câmera': 'Camera effects',
  Efeitos: 'Effects',
  'Espelhar minha imagem': 'Mirror my video',
  'Como no espelho. Só muda o que você vê, não o que ela recebe.':
    'Like a mirror. It only changes what you see, not what the other person gets.',
  'Fechar o aviso': 'Close the notice',
  'Fechar trailer': 'Close trailer',
  'Ver o trailer': 'Watch the trailer',
  Assistir: 'Watch',
  'ESCOLHA UM EPISÓDIO PARA VER': 'PICK AN EPISODE TO WATCH',
  'Nenhum episódio nesta temporada': 'No episode in this season',
  'Sem informações de elenco': 'No cast information',
  'Nenhuma avaliação ainda': 'No rating yet',
  'Nenhum título parecido': 'No similar title',
  Temporada: 'Season',
  'Modo cinema': 'Cinema mode',
  'Trocar fonte': 'Change source',
  'Sair do modo cinema': 'Leave cinema mode',
  'Preparando o stream': 'Preparing the stream',
  Legendas: 'Subtitles',

  // -- O anime: a busca, a grade, a lista de cada um e o aviso de direitos.
  'Sair do anime': 'Leave anime',
  'Buscar anime': 'Search anime',
  'Títulos em inglês': 'English titles',
  'Títulos em japonês': 'Japanese titles',
  'Direitos autorais e remoção': 'Copyright and takedown',
  'A seção de anime do Nosso Sempre é uma biblioteca particular, feita para quem mora nesta casa. Ela não hospeda nenhum arquivo de vídeo: a reprodução vem de players públicos de terceiros, e toda a arte, os títulos e os metadados pertencem a seus respectivos donos.':
    'The anime section of Nosso Sempre is a private library, made for the people who live in this home. It hosts no video file of its own: playback comes from public third-party players, and all the art, titles and metadata belong to their respective owners.',
  'Se você acredita que algum conteúdo aqui viola seus direitos autorais, entre em contato informando o título exato, o episódio e o player, que removemos ou bloqueamos o acesso ao material o quanto antes.':
    'If you believe any content here infringes your copyright, get in touch with the exact title, the episode and the player, and we will remove or block access to the material as soon as possible.',
  'Aviso: o Nosso Sempre é um projeto pessoal, sem publicidade e sem nenhuma monetização. Toda a reprodução acontece dentro de players de terceiros, fornecidos por plataformas externas.':
    'Notice: Nosso Sempre is a personal project, with no advertising and no monetization at all. All playback happens inside third-party players, provided by external platforms.',
  'Seções do anime': 'Anime sections',
  'Idioma do título': 'Title language',
  BUSCAR: 'SEARCH',
  'Nome do anime': 'Anime name',
  Ano: 'Year',
  Ordenar: 'Sort',
  Tipo: 'Type',
  Gênero: 'Genre',
  Situação: 'Status',
  'Todos os animes': 'All anime',
  'Filmes de anime': 'Anime movies',
  'Todos os gêneros': 'All genres',
  'Todos os anos': 'All years',
  Qualquer: 'Any',
  'No ar': 'Airing',
  Finalizado: 'Finished',
  'Comece a digitar para buscar anime.': 'Start typing to search anime.',
  'Grade de exibição': 'Airing schedule',
  'Veja o que estreia esta semana, nos 7 dias.': 'See what airs this week, all 7 days.',
  'Veja o que estreia esta semana, nos 7 dias': 'See what airs this week, all 7 days',
  'Dia da semana': 'Day of the week',
  'Nada no ar neste dia': 'Nothing airing on this day',
  Histórico: 'History',
  'Suas coleções': 'Your collections',
  'NOME DA PLAYLIST': 'PLAYLIST NAME',
  'Nome da playlist': 'Playlist name',
  'Para ver junto': 'To watch together',
  DESENHO: 'COVER',
  'Nada visto ainda': 'Nothing watched yet',
  'Minha lista': 'My list',
  'Por temporada': 'By season',
  'Sua lista está vazia. Salve animes pela busca.':
    'Your list is empty. Save anime from the search.',
  'Esta playlist não existe mais.': 'This playlist does not exist anymore.',
  'Esta playlist está vazia': 'This playlist is empty',
  'Nenhuma playlist ainda. Crie a primeira.': 'No playlist yet. Create the first one.',
  Desenho: 'Cover',
  'Desenho da playlist': 'Playlist cover',
  'Ver tudo': 'See all',
  'Em destaque': 'Featured',
  'AnimeX, o início': 'AnimeX, the start',
  'A seção de anime do Nosso Sempre.': 'The anime section of Nosso Sempre.',
  'Direitos autorais': 'Copyright',
  '© 2026 Nosso Sempre. Só para se divertir.': '© 2026 Nosso Sempre. Just for fun.',
  'Abre a lista de opções': 'Opens the options list',
  Desconhecido: 'Unknown',
  Páginas: 'Pages',
  'Este título não tem cruzamento no MyAnimeList, então não dá para abrir o tocador.':
    'This title has no MyAnimeList match, so the player cannot open.',
  'Não deu para carregar este título agora.': 'Could not load this title right now.',
  'Lista de episódios indisponível': 'Episode list unavailable',
  'Páginas de episódios': 'Episode pages',
  'Salvar na playlist': 'Save to playlist',
  'Nenhuma playlist ainda': 'No playlist yet',

  // -- O Kovu, as lembranças, as cartas e o quadro: o lado de guardar coisas.
  'Livro de Memórias': 'Book of Memories',
  'Quiz de Memórias': 'Memory Quiz',
  'Kovu Hoje': 'Kovu Today',
  'Nova conversa': 'New chat',
  'Escreva para o Kovu': 'Write to Kovu',
  'Escreva aqui': 'Write here',
  Enviar: 'Send',
  Conversa: 'Chat',
  'Nenhum humor registrado hoje.': 'No mood logged today.',
  'A lista está quietinha agora.': 'The list is quiet right now.',
  'Adicionar memória': 'Add memory',
  'Caderno do Kovu': "Kovu's notebook",
  'Neste Dia': 'On This Day',
  'Nenhuma memória aqui ainda. Conte algo para o Kovu guardar.':
    'No memory here yet. Tell Kovu something to keep.',
  'Esquecer esta memória?': 'Forget this memory?',
  Manter: 'Keep',
  Esquecer: 'Forget',
  'Buscar memórias...': 'Search memories...',
  'Buscar memórias': 'Search memories',
  'Nova memória': 'New memory',
  'A memória': 'The memory',
  'Quando aconteceu': 'When it happened',
  Lembrar: 'Remember',
  'Tipo de memória': 'Memory type',
  'Ainda não há memórias suficientes. Peça para o Kovu guardar algo primeiro!':
    'There are not enough memories yet. Ask Kovu to keep something first!',
  'Memórias respondidas': 'Memories answered',
  'Jogar de novo': 'Play again',
  'Sortear uma lembrança': 'Draw a memory',
  'O que foi bom?': 'What was good?',
  'Uma frase que valha reler daqui a um ano': 'A line worth rereading a year from now',
  Guardar: 'Keep',
  'Apagar lembrança': 'Delete memory',
  'Sortear uma': 'Draw one',
  'Arquivo de cartas': 'Letter archive',
  'Buscar cartas': 'Search letters',
  'Ainda lacrada': 'Still sealed',
  'Vou esperar': 'I will wait',
  'Filtrar as cartas': 'Filter the letters',
  'Adicione uma foto': 'Add a photo',
  'Galeria de memórias': 'Memory gallery',
  'Adicionar foto': 'Add photo',
  'A foto não abriu': 'The photo did not open',
  'O que vocês querem fazer?': 'What do the two of you want to do?',
  'Ver a neve, aprender a cozinhar...': 'See snow, learn to cook...',
  Categoria: 'Category',
  'Progresso da lista': 'List progress',
  'Filtro da lista': 'List filter',
  'Abrindo o quadro': 'Opening the canvas',
  'Desenhem alguma coisa juntos': 'Draw something together',
  'Folha de desenho': 'Drawing sheet',
  Caneta: 'Pen',
  Borracha: 'Eraser',
  Texto: 'Text',
  Desfazer: 'Undo',
  Refazer: 'Redo',
  'Limpar o quadro': 'Clear the canvas',
  'Texto do recado': 'Note text',
  'Escreva algo...': 'Write something...',
  'Espessura do traço': 'Stroke width',
  'Adicionar texto': 'Add text',

  // -- O refúgio: o santuário de mensagens entre os dois.
  'Conferir a conexão': 'Check the connection',
  Refúgio: 'Refuge',
  'Conferindo o acesso...': 'Checking access...',
  'Abrindo o Santuário...': 'Opening the Sanctuary...',
  'Demorando demais? Toque para tentar de novo': 'Taking too long? Tap to try again',
  'O Santuário está vazio...': 'The Sanctuary is empty...',
  'Mande a primeira mensagem para começar a florescer': 'Send the first message and watch it bloom',
  'Ir para o fim': 'Go to the end',
  'Escreva uma mensagem': 'Write a message',
  'Escreva uma mensagem...': 'Write a message...',
  'Como está o Refúgio': 'How the Refuge is doing',
  'Testando a conexão com o Santuário...': 'Testing the connection to the Sanctuary...',
  'Sem ligação com a outra pessoa · entre de novo': 'No link to the other person · come back in',
  'Reiniciar e tentar': 'Restart and try',

  // -- O calendário e os calendários assinados.
  'Marcar uma data': 'Mark a date',
  'Calendários assinados': 'Subscribed calendars',
  Atualizar: 'Refresh',
  Calendário: 'Calendar',
  'Em breve': 'Coming up',
  'Marcar aqui': 'Mark here',
  'Mês anterior': 'Previous month',
  'Voltar para hoje': 'Back to today',
  'Próximo mês': 'Next month',
  'nada marcado': 'nothing marked',
  'O que é': 'What it is',
  'Jantar de aniversário': 'Anniversary dinner',
  Detalhe: 'Detail',
  'O que for bom lembrar': 'Whatever is worth remembering',
  Repete: 'Repeats',
  'Nada marcado por enquanto': 'Nothing marked for now',
  'Nada marcado neste dia': 'Nothing marked on this day',
  'Novo calendário': 'New calendar',
  Calendários: 'Calendars',
  'OS MEUS': 'MINE',
  'Você ainda não assinou nenhum.': 'You have not subscribed to any yet.',
  'COMPARTILHADOS COM VOCÊ': 'SHARED WITH YOU',
  'Nada compartilhado ainda.': 'Nothing shared yet.',
  'Trabalho, aniversários, academia...': 'Work, birthdays, gym...',
  'Assinar um link': 'Subscribe to a link',
  'Só no app': 'App only',
  'O link fica só no servidor. Quem você compartilhar vê os eventos, nunca o endereço.':
    'The link stays on the server only. Whoever you share it with sees the events, never the address.',
  'Compartilhar com a outra pessoa': 'Share with the other person',
  Guardando: 'Saving',
  // Com reticências é o botão das folhas de guardar foto e marco, que trocam de
  // palavra enquanto a subida corre. Verbete próprio porque a chave é a frase
  // inteira, e "Guardando…" não é "Guardando".
  'Guardando…': 'Saving…',
  'Os meus': 'Mine',
  'Compartilhados com você': 'Shared with you',
  'Buscando os eventos': 'Fetching the events',
  'Tipo de calendário': 'Calendar type',

  // -- As finanças.
  Finanças: 'Finances',
  'Abrindo as finanças': 'Opening the finances',
  'Alguma coisa deu errado': 'Something went wrong',
  'Ambiente de teste do Sicredi. Estes números são de mentira.':
    'Sicredi test environment. These numbers are fake.',
  'Saldo agora': 'Balance now',
  'Mês seguinte': 'Next month',
  'Nenhum movimento neste mês.': 'No transaction this month.',

  // -- A academia e a zona de jogos.
  'Central da Academia': 'Academy Hub',
  Procurando: 'Searching',
  'Cancelar busca': 'Cancel search',
  'Não achamos ninguém para jogar com você agora. Quer jogar no modo Solo?':
    'We could not find anyone to play with you right now. Want to play Solo?',
  'Voltar ao início': 'Back to the start',
  'Travado por 2 segundos...': 'Locked for 2 seconds...',
  'Perguntas respondidas': 'Questions answered',
  'Buscando perguntas novas...': 'Fetching new questions...',
  'Zona de Jogos': 'Game Zone',
  'Abra caminho na chave do torneio mundial': 'Work your way through the world tournament bracket',
  'Recomeçar a partida': 'Restart the match',
  'Gire o aparelho': 'Turn your device',
  'Jogue contra seu par': 'Play against your partner',
  'Procurar partida': 'Find a match',

  // -- Os ajustes: o código, a casa, as chaves de acesso, os avisos e as contas.
  'Código atual': 'Current code',
  'Novo código': 'New code',
  'Repita o novo código': 'Repeat the new code',
  'Salvar novo código': 'Save new code',
  'Trocar com ajuda': 'Change with help',
  'Meu novo código': 'My new code',
  'Trocar meu código': 'Change my code',
  'Quem está do seu lado': 'Who is by your side',
  'Com isso ligado, uma mensagem no santuário, um humor novo, uma estrela ou uma foto chegam como aviso aqui neste aparelho, mesmo com o app fechado. O aviso diz só quem foi: o que foi escrito fica guardado e só aparece quando você abre.':
    'With this on, a message in the sanctuary, a new mood, a star or a photo arrive as a notification on this device, even with the app closed. The notification says only who it was: what was written stays kept and only shows up when you open the app.',
  'Com isso ligado, uma mensagem no santuário, um humor novo, uma estrela ou uma foto chegam como aviso aqui neste aparelho, mesmo com o app fechado. O aviso não carrega o que foi escrito: o texto só é buscado quando você abre.':
    'With this on, a message in the sanctuary, a new mood, a star or a photo arrive as a notification on this device, even with the app closed. The notification does not carry what was written: the text is only fetched when you open the app.',
  'Verificando este aparelho...': 'Checking this device...',
  'Enviar um teste': 'Send a test',
  'Quem mora aqui': 'Who lives here',
  Pronomes: 'Pronouns',
  Fuso: 'Time zone',
  Cor: 'Color',
  'Mais uma pessoa': 'One more person',
  Aceitar: 'Accept',
  Recusar: 'Decline',
  'Começou em': 'Started on',
  'Voltar tudo ao padrão': 'Reset everything',
  'Tirar da casa': 'Remove from the home',
  'Uma chave de acesso deixa você entrar sem digitar nada. Para mexer nelas, confirme seu código.':
    'A passkey lets you come in without typing anything. To manage them, confirm your code.',
  'Seu código': 'Your code',
  'Ver minhas chaves': 'See my passkeys',
  'Criar chave neste aparelho': 'Create a passkey on this device',
  'Este aparelho não sabe criar uma chave de acesso. Daqui você renomeia e remove as que já existem, inclusive a de um aparelho perdido.':
    'This device cannot create a passkey. From here you can rename and remove the ones that already exist, including the one on a lost device.',
  'Esse dispositivo não vai mais abrir o app sozinho. Seu código continua funcionando.':
    'That device will no longer open the app on its own. Your code still works.',
  'Este navegador não trabalha com chaves de acesso. Seu código de 4 dígitos continua abrindo o app normalmente.':
    'This browser does not work with passkeys. Your 4-digit code still opens the app just fine.',
  // Os quatro recados do `nucleo/chaves.ts`. Ficam em português lá e passam
  // pelo `traduzir()` na hora de aparecer, como o `nome` dos `DESTINOS`.
  'Este navegador não suporta chaves de acesso.': 'This browser does not support passkeys.',
  'Esse dispositivo já tem uma chave salva aqui.': 'This device already has a passkey saved here.',
  'Chaves só funcionam em uma conexão segura.': 'Passkeys only work over a secure connection.',
  'Não foi possível usar sua chave neste dispositivo.': 'Your passkey could not be used on this device.',
  'Você ainda não tem nenhuma chave. Crie uma neste aparelho e da próxima vez o app abre com a digital.':
    'You do not have any passkeys yet. Create one on this device and next time the app opens with your fingerprint.',
  Renomear: 'Rename',
  'O app não pede senha e não entra na sua conta. Só guarda o endereço para o link abrir no seu perfil, e não no da outra pessoa.':
    "The app does not ask for a password and does not sign in to your account. It only keeps the address so the link opens in your profile, and not in the other person's.",
  'Sua conta precisa estar logada neste navegador. Guarde o número dela: 0 para a primeira do seletor do Google, 1 para a segunda, e assim por diante. Só com o número dá para trocar a conta por aqui.':
    "Your account has to be signed in on this browser. Keep its number: 0 for the first one in Google's picker, 1 for the second, and so on. Only the number makes switching accounts from here possible.",
  'Se este navegador só tem a sua conta aberta, o YouTube Music já abre nela e não há nada para trocar. Guardar o e-mail aqui serve para o app saber de quem é a conta.':
    'If this browser only has your account open, YouTube Music already opens in it and there is nothing to switch. Keeping the e-mail here is what tells the app whose account it is.',
  'Sua conta do YouTube Music': 'Your YouTube Music account',
  'Salvar conta': 'Save account',
  'Deixar essa conta ativa no YouTube Music': 'Make that the active account on YouTube Music',
  'Seu Last.fm': 'Your Last.fm',
  'É daqui que o painel lê o que você está ouvindo, e é de propósito que a casa toda enxerga: a graça é a outra pessoa ver. Não é senha nem login, é só o seu nome de usuário lá.':
    'This is where the dashboard reads what you are listening to, and the whole home seeing it is the point: the fun is the other person seeing. It is not a password or a login, just your username over there.',
  'Seu nome de usuário no Last.fm': 'Your Last.fm username',
  'Salvar Last.fm': 'Save Last.fm',
  'A casa ainda não tem a chave do Last.fm, então o painel não consegue ler o que ninguém ouve. Peça uma chave gratuita em last.fm/api/account/create e ponha LASTFM_API_KEY no api.env do servidor.':
    'The home does not have the Last.fm key yet, so the dashboard cannot read what anyone is listening to. Ask for a free key at last.fm/api/account/create and put LASTFM_API_KEY in the server api.env.',
  'Para o que você ouve aparecer no painel': 'To make what you listen to show up on the dashboard',
  'O YouTube Music não avisa ninguém sobre o que está tocando. Quem conta para o painel é o Last.fm, e ele precisa de um scrobbler instalado no navegador. O Web Scrobbler (web-scrobbler.com) já vem com suporte ao YouTube Music.':
    'YouTube Music does not tell anyone what is playing. The one that tells the dashboard is Last.fm, and it needs a scrobbler installed in the browser. Web Scrobbler (web-scrobbler.com) already supports YouTube Music.',
  Testar: 'Test',
  Desconectar: 'Disconnect',
  'Endereço do site': 'Site address',
  'Cookies da sessão': 'Session cookies',
  'O que você colar fica só no servidor. A tela mostra o nome dos cookies, nunca o valor.':
    'What you paste stays on the server only. The screen shows the cookie names, never the values.',
  'Ligar conta': 'Link account',
  'Ligar uma conta': 'Link an account',
  'A chave aparece uma vez só, na hora em que é criada. Se você a perder, revogue a antiga por lá e crie outra.':
    'The key shows up only once, right when it is created. If you lose it, revoke the old one over there and create another.',
  'A chave da conta de IPTV': 'The IPTV account key',
  'Abrir iptv.facilemacile.ltd': 'Open iptv.facilemacile.ltd',
  'O site da conta': 'The account site',
  'nome=valor; outro=valor': 'name=value; other=value',
  'Uma extensão de exportar cookies: serve o arquivo cookies.txt e serve o JSON, colados inteiros.':
    'A cookie-export extension: the cookies.txt file works and so does its JSON, pasted whole.',
  'Ou abra o site já logado, aperte F12, aba Rede, clique em qualquer chamada e copie o cabeçalho Cookie.':
    'Or open the site already signed in, press F12, Network tab, click any request and copy the Cookie header.',
  'O console (document.cookie) não serve: ele esconde o cookie de login.':
    'The console (document.cookie) will not do: it hides the login cookie.',

  // -- Os rótulos de botão que dividem o pai com um ícone.
  //
  // Na web eles são irmãos do `<Icone />`, e no celular o mesmo botão põe o
  // rótulo dentro de um `<Text>`. Por isso boa parte destas chaves já existia
  // lá em cima: quem as trouxe foi a tela gêmea do celular.
  'Acesso rápido': 'Quick access',
  Criar: 'Create',
  'Limpar tudo': 'Clear all',
  'Marcar o próximo encontro': 'Mark the next date',
  'Marcar um': 'Mark one',
  Nova: 'New',
  'Página embutida': 'Embedded page',
  'Trocar de servidor': 'Change server',

  // -- As frases que viajam como propriedade: título de seção, recado de tela
  // vazia, dica embaixo do título.
  //
  // Em minúscula as que a tela desenha em minúscula -- são subtítulos, e o
  // inglês mantém a mesma voz baixa do português.
  'A casa': 'The home',
  'A distância': 'The distance',
  'A história de vocês': 'Your story',
  'A relação': 'The relationship',
  'O momento': 'The moment',
  'Código de acesso': 'Access code',
  'quatro dígitos, só seus': 'four digits, yours alone',
  'Esqueci meu código': 'I forgot my code',
  'Chaves de acesso': 'Passkeys',
  'entrar com a digital, o rosto ou o PIN do aparelho':
    'come in with your fingerprint, your face or the device PIN',
  Notificações: 'Notifications',
  'Contas ligadas': 'Linked accounts',
  'cada pessoa liga a sua, colando os cookies da sessão já aberta':
    'each person links their own, pasting the cookies from a session already open',
  Música: 'Music',
  'sua conta do YouTube Music e seu Last.fm': 'your YouTube Music account and your Last.fm',
  '0, 1, 2... ou voce@gmail.com': '0, 1, 2... or you@gmail.com',
  'a chave da conta do serviço de IPTV': 'the key to the IPTV service account',
  'Conexão com a Amazon': 'Amazon connection',
  'Listas na Alexa': 'Lists on Alexa',
  'As listas que vocês já importaram. Dá para trazer o que está lá ou mandar o que falta comprar.':
    'The lists the two of you have imported. You can bring over what is there or send what is still to buy.',
  'o mercado e os lembretes, do Echo para cá':
    'the groceries and the reminders, from the Echo to here',
  'escolha o que vocês dois veem': 'pick what the two of you see',
  'Pedidos esperando você': 'Requests waiting for you',
  'Quem administra': 'Who administers',
  'quem mora aqui, e de que jeito': 'who lives here, and in what way',
  'como você quer ser chamada ou chamado': 'how you want to be called',
  'ela/dela, ele/dele, elu/delu': 'she/her, he/him, they/them',
  'a cor do app é sua, e só sua': 'the app color is yours, and yours alone',
  'sua conta e seu jeito de entrar': 'your account and how you come in',
  'Recursos do app': 'App features',
  'o que existe para este casal': 'what there is for this couple',
  'o dia em que tudo começou': 'the day it all started',
  'as datas de vocês': 'your dates',
  'Acesso restrito': 'Restricted access',
  'Você precisa entrar': 'You need to come in',
  'Volte e entre com o seu código.': 'Go back and come in with your code.',
  'O Santuário é só para os dois.': 'The Sanctuary is just for the two of you.',

  // -- Os cartões do painel e as telas vazias.
  'O Kovu está aqui': 'Kovu is here',
  'O Kovu diz…': 'Kovu says…',
  'O que o Kovu percebeu': 'What Kovu noticed',
  'o que o Kovu lembra de vocês': 'what Kovu remembers about the two of you',
  'Pergunte qualquer coisa sobre a casa, os planos ou o dia.':
    'Ask anything about the home, the plans or the day.',
  'o dia de vocês num fôlego só': 'your whole day in one breath',
  'quem está usando o app agora': 'who is using the app right now',
  'Pote de estrelas': 'Jar of stars',
  'gratidão sob as estrelas': 'gratitude under the stars',
  'O pote está vazio': 'The jar is empty',
  'Guardar uma foto': 'Keep a photo',
  'o álbum de vocês': 'your album',
  'Escreva a primeira lembrança aí em cima.': 'Write the first memory up there.',
  'Escreva o primeiro sonho aí em cima.': 'Write the first dream up there.',
  'sonhos que a gente persegue junto': 'dreams we chase together',
  'Próximas datas': 'Upcoming dates',
  'Vem aí': 'Coming soon',
  'Lista de filmes e Estrelas': 'Movie list and Stars',
  'Nada nesta lista': 'Nothing in this list',
  'Escolha uma categoria': 'Pick a category',
  'Escolha sua planta': 'Pick your plant',
  'Cada planta tem um mês de bônus na estação dela':
    'Each plant gets a bonus month in its own season',
  'Nenhum filme visto ainda. Começa uma sessão!': 'No movie watched yet. Start a session!',
  'Nada rolando agora. Começa um filme ou uma série!':
    'Nothing going on right now. Start a movie or a series!',
  'Nenhum anime visto ainda. Bora maratonar!': 'No anime watched yet. Time to binge!',
  'Ouvidos há pouco': 'Heard recently',
  'Assistindo agora': 'Watching now',

  // -- A cozinha, o caderno e o quadro.
  'o que a gente sabe fazer': 'what we know how to make',
  'É por ele que a lista de compras se organiza.': 'It is what the shopping list is organized by.',
  'Essa receita não está mais no livro': 'That recipe is not in the book anymore',
  'Não deu para carregar as receitas': 'Could not load the recipes',
  'Apagar receita?': 'Delete the recipe?',
  'Isso não dá para desfazer.': 'This cannot be undone.',
  'Cole o endereço da receita. Dá para conferir e corrigir tudo antes de salvar.':
    'Paste the recipe address. You can check and fix everything before saving.',
  'Tente outro ingrediente.': 'Try another ingredient.',
  'Escreva a primeira, ou cole o link de uma que vocês gostam.':
    'Write the first one, or paste a link to one the two of you like.',
  'O livro ainda está em branco': 'The book is still blank',
  'Apagar compromisso?': 'Delete the event?',
  'Apagar a foto?': 'Delete the photo?',
  'Limpar o quadro?': 'Clear the canvas?',
  'Isso apaga todos os rabiscos para os dois, de vez. Tem certeza?':
    'This erases every scribble for both of you, for good. Are you sure?',
  'o quanto vocês lembram da própria história?':
    'how much do the two of you remember of your own story?',

  // -- Os livros: as prateleiras e os recados de vazio.
  'Continuar lendo': 'Keep reading',
  'Abra aquele livro de novo': 'Open that book again',
  'Esta semana': 'This week',
  'Volte logo, a lista muda toda semana.': 'Come back soon, the list changes every week.',
  'Busque qualquer livro, autor ou assunto no acervo inteiro.':
    'Search any book, author or subject in the whole collection.',
  'Escreva para achar': 'Type to find',
  'Tente outro título, autor ou assunto. O acervo cobre a Open Library, o Project Gutenberg e o Internet Archive.':
    'Try another title, author or subject. The collection covers Open Library, Project Gutenberg and the Internet Archive.',
  'Dá uma olhada na aba de busca para achar mais livros.':
    'Take a look at the search tab to find more books.',
  'Nenhum arquivo para baixar neste título.': 'No file to download for this title.',
  Avaliações: 'Reviews',

  // -- O cinema e o anime: as fileiras da tela inicial.
  'Em alta agora': 'Trending now',
  'Em cartaz': 'In theaters',
  Lançamentos: 'New releases',
  'Filmes populares': 'Popular movies',
  'Séries populares': 'Popular series',
  'Mais bem avaliados': 'Top rated',
  'Séries mais bem avaliadas': 'Top rated series',
  'Top 10 de hoje': "Today's Top 10",
  'Só na Netflix': 'Netflix only',
  'Só no Prime Video': 'Prime Video only',
  'Nada encontrado. Tente outro título.': 'Nothing found. Try another title.',
  'Nada com esse nome': 'Nothing by that name',
  'Escolha um servidor de streaming': 'Pick a streaming server',
  'Provedores embutidos padrão': 'Default embedded providers',
  'Busque os filmes que o Jellyfin indexou das suas pastas.':
    'Search the movies Jellyfin indexed from your folders.',
  'Busque um filme para adicionar à biblioteca da sessão.':
    'Search a movie to add to the session library.',
  'O QUE ESTÃO FALANDO': 'WHAT PEOPLE ARE SAYING',
  'DESCUBRA MAIS': 'DISCOVER MORE',
  'Todos os episódios': 'All episodes',
  'No ar agora': 'Airing now',
  'No ar hoje': 'Airing today',
  'Animes recomendados': 'Recommended anime',
  'As fontes de anime estão fora do ar agora.': 'The anime sources are down right now.',
  'o que cada um acompanha, e o que vocês dividem':
    'what each of you follows, and what the two of you share',

  // -- A academia e os jogos.
  'jogar, aprender, competir': 'play, learn, compete',
  'jogos para dois': 'games for two',
  'Treinar sozinho': 'Train alone',
  'Disputar com seu par': 'Compete with your partner',
  'Nenhum ranking disponível': 'No ranking available',
  'O Ping-pong funciona com o aparelho deitado.': 'Ping-pong works with the device on its side.',
  'O ping-pong roda deitado.': 'Ping-pong runs on its side.',

  // -- Os moldes: frases que uma conta ou um nome corta no meio.
  //
  // O `{nome}` entre chaves é buraco, e `preencher()` põe o valor lá. O inglês
  // tem de repetir os mesmos buracos -- se faltar um, o teste de paridade grita.
  //
  // Quando o português muda de frase conforme o número, cada número é um molde
  // inteiro seu: é o código que escolhe qual, e o inglês recebe a frase toda.
  '{feitos} de {total} sonhos realizados': '{feitos} of {total} dreams come true',
  '{feitos} de {total} sonho realizado': '{feitos} of {total} dream come true',
  '{parte}% do caminho': '{parte}% of the way',
  '{parte} por cento realizados': '{parte} per cent come true',
  'Memória {n} de {total}': 'Memory {n} of {total}',
  '{pontos} / {total} memórias': '{pontos} / {total} memories',
  'Perfeito! O Kovu está orgulhoso': 'Perfect! Kovu is proud',
  'Quase lá': 'Almost there',
  'Ver pontuação': 'See the score',
  'Próxima memória': 'Next memory',
  'Escrita para {n} porções': 'Written for {n} servings',
  'A receita foi escrita para {n}': 'The recipe was written for {n}',
  'Veio de {onde}': 'Came from {onde}',
  'Esta carta abre em {quando}.': 'This letter opens in {quando}.',
  'falta {itens}': 'missing {itens}',
  'vence em {quando}': 'goes off {quando}',
  'Dá para fazer agora': 'Can be made now',
  'Falta comprar': 'Still to buy',
  'Mostrando os {n} movimentos mais recentes do mês.':
    'Showing the {n} most recent entries of the month.',
  'desde {quando}': 'since {quando}',
  '{nome} está por aqui': '{nome} is around',
  '{nome} · ainda não apareceu': '{nome} · has not shown up yet',
  'Ativa há {faz}': 'Around for {faz}',
  'O Kovu vai deixar de lembrar: {fato}': 'Kovu will stop remembering: {fato}',
  'Não deu para carregar o quadro: {erro}': 'Could not load the canvas: {erro}',

  // -- Os ajustes: a casa, as contas e o código.
  'com {quem}': 'with {quem}',
  'Casa {id}': 'Home {id}',
  'ao vivo': 'live',
  'sem fluxo ao vivo': 'no live feed',
  'precisa de {quem} por perto': 'needs {quem} nearby',
  'Um código só pode ser trocado sem o antigo se {quem} digitar o dela ou dele aqui. É o que impede que alguém troque seu código sozinho, e é também a saída se você esquecer o seu.':
    'A code can only be changed without the old one if {quem} types theirs in here. That is what keeps anyone from changing your code on their own, and it is also the way out if you forget yours.',
  'Quem administra a casa é {lider}. Você pode escrever aqui do jeito que acha que deveria ficar: ao salvar, o pedido vai para {lider} responder, e só muda para todo mundo depois disso.':
    'The one who runs the house is {lider}. You can write here the way you think it should read: when you save, the request goes to {lider} to answer, and it only changes for everyone after that.',
  Alguém: 'Someone',
  'ajustes na casa': 'settings in the house',
  '{quem} quer {resumo}': '{quem} wants {resumo}',
  'Guardado como “{guardada}”. O botão de troca só aparece com o número da conta, porque é a única coisa que o YouTube Music aceita no endereço. Com o e-mail ele pede senha, e o app não faz isso.':
    'Saved as “{guardada}”. The switch button only shows up with the account number, because that is the only thing YouTube Music takes in the address. With the e-mail it asks for a password, and the app does not do that.',
  '{erro} A conta continua ligada, e as listas já importadas seguem aqui. Assim que a Amazon voltar, elas atualizam sozinhas.':
    '{erro} The account is still linked, and the lists already imported are still here. As soon as Amazon is back, they refresh on their own.',

  // -- O cinema junto: a sala, a conversa e o tocador.
  'Ao vivo com {quem} · some quando quiser': 'Live with {quem} · slip away whenever you like',
  'Manda um oi para {quem} enquanto escolhem o filme.':
    'Say hi to {quem} while the two of you pick the film.',
  'Chat com {quem}': 'Chat with {quem}',
  '{quem} abriu uma sessão de cinema': '{quem} opened a cinema session',
  'Assistindo junto com {quem}': 'Watching along with {quem}',
  'Pausado por quem convidou': 'Paused by whoever invited you',
  'Sincronizado ao vivo': 'Synced live',
  'Servidor: {nome}': 'Server: {nome}',
  'Stream HLS próprio · play/pause/avanço sincronizados de verdade':
    'Own HLS stream · play/pause/seek truly in sync',
  'Servidor embutido · sincronia aproximada': 'Embedded server · sync is approximate',
  RETOMAR: 'RESUME',
  PAUSAR: 'PAUSE',
  'Você está em {tempo}': 'You are at {tempo}',
  'Este título não está disponível em {fonte}.': 'This title is not available on {fonte}.',
  'nenhuma fonte': 'no source',
  '{n} fontes': '{n} sources',

  // -- Os livros: o leitor e a prateleira compartilhada.
  'Abrir no {onde}': 'Open in {onde}',
  '{n} de {total}': '{n} of {total}',
  'Isso tira “{titulo}” da lista compartilhada de todo mundo.':
    'This takes “{titulo}” off the list everyone shares.',
  'Narrado por {quem}': 'Narrated by {quem}',
  // -- A quarta passada: a frase escrita dentro da chave do JSX.
  //
  // Ternário, `??` e molde de crase. O codemod só reescreve o que está em
  // posição de desenho -- nunca a condição -- então o que chega aqui é fala,
  // e não operando de comparação. Frase que já nasce em inglês lá em cima
  // não aparece aqui: sem verbete, `traduzirPara` devolve a própria chave.
  'Ir para {nome}': 'Go to {nome}',
  '{faltam} XP para o nível {v}': '{faltam} XP to level {v}',
  '{feitos} de {pontos_por_nivel} pontos do nível {nivel}':
    '{feitos} of {pontos_por_nivel} points in level {nivel}',
  'Voltar {pulo} segundos': 'Back {pulo} seconds',
  'Adiantar {pulo} segundos': 'Forward {pulo} seconds',
  // O espaço dobrado é do desenho da capa, não engano: o inglês repete.
  'DESDE  {v}  ·  {v2}': 'SINCE  {v}  ·  {v2}',
  'Modo IA ligado. Toque para voltar ao sorteio.': 'AI mode on. Tap to go back to the draw.',
  'Modo sorteio. Toque para ligar a IA.': 'Draw mode. Tap to turn the AI on.',
  'DECIDINDO O DESTINO...': 'DECIDING FATE...',
  'TOQUE NO CORAÇÃO': 'TAP THE HEART',
  'Sua {v}, fase {fase} de 5. Toque para ver.': 'Your {v}, stage {fase} of 5. Tap to see.',
  'Humor {emoji}': 'Mood {emoji}',
  '{usuario} · mais ouvidos de sempre': '{usuario} · all-time most played',
  '1 item em aberto': '1 item still open',
  '{abertos} itens em aberto': '{abertos} items still open',
  'Nada em aberto para mandar': 'Nothing open to send',
  'Corrigir receita': 'Edit recipe',
  'Corredor de {nome}: {v}': '{nome} aisle: {v}',
  'Tirar {nome}': 'Remove {nome}',
  'Trocar a foto de perfil': 'Change the profile photo',
  'Escolher foto de perfil': 'Choose a profile photo',
  'Ao vivo do Last.fm': 'Live from Last.fm',
  'O que a gente está ouvindo': 'What we are listening to',
  'NO AR': 'ON AIR',
  'EM ESPERA': 'STANDING BY',
  '{nome} está ouvindo...': '{nome} is listening...',
  'Memória do álbum': 'Album memory',
  'Tirar #{m}': 'Remove #{m}',
  'Tirar a etiqueta {t}': 'Remove the {t} tag',
  'Lista de sonhos: {feitos} de {total} realizados. Toque para abrir.':
    'Dream list: {feitos} of {total} come true. Tap to open.',
  'Foto de lembrança': 'Keepsake photo',
  'Clique para caber na tela': 'Click to fit the screen',
  'Clique para ver de perto': 'Click to zoom in',

  // -- Os ajustes, a casa e as contas conectadas.
  'Código de {nomeDoParceiro}': '{nomeDoParceiro} code',
  'saber na hora quando {nomeDosOutros} aparecer': 'know the moment {nomeDosOutros} shows up',
  'Desligar neste aparelho': 'Turn off on this device',
  'Ligar notificações': 'Turn notifications on',
  '1 aparelho recebendo avisos.': '1 device receiving alerts.',
  '{aparelhos} aparelhos recebendo avisos.': '{aparelhos} devices receiving alerts.',
  'Tirar {v} da casa': 'Remove {v} from the household',
  'esta pessoa': 'this person',
  'Salvar a casa': 'Save the household',
  'Sugerir estas mudanças': 'Suggest these changes',
  'uma mudança que alguém da casa quer': 'one change someone here wants',
  '{pedidos} mudanças que alguém da casa quer': '{pedidos} changes someone here wants',
  'O que estiver desligado some do app para todo mundo desta casa. Cada casa tem o seu conjunto, então ligar algo aqui não muda nada para ninguém mais.':
    'Whatever is off disappears from the app for everyone in this household. Each household has its own set, so turning something on here changes nothing for anyone else.',
  'Quem administra a casa é quem muda isto. O que você mexer aqui vira um pedido, e muda para todo mundo assim que for aceito.':
    'Whoever runs the household is the one who changes this. What you touch here becomes a request, and changes for everyone as soon as it is accepted.',
  'Você ainda não tem nenhuma chave. Crie uma no site ou num iPhone e da próxima vez ele abre com a digital.':
    'You have no key yet. Create one on the site or on an iPhone and next time it opens with your fingerprint.',
  'Renomear {rotulo}': 'Rename {rotulo}',
  'Remover {rotulo}': 'Remove {rotulo}',
  'Nome desta chave': 'Name of this key',
  'Remover "{v}"?': 'Remove "{v}"?',
  'Instale a extensão, conecte no Last.fm e escreva aqui em cima o seu nome de usuário de lá. Sem ele o painel não tem onde ler o que você ouve.':
    'Install the extension, connect it to Last.fm and write your username from there up above. Without it the panel has nowhere to read what you listen to.',
  'Instale a extensão, conecte no Last.fm e deixe o scrobbler ligado para o YouTube Music. A conta do Last.fm que este painel lê para você é "{guardadoLastfm}".':
    'Install the extension, connect it to Last.fm and leave the scrobbler on for YouTube Music. The Last.fm account this panel reads for you is "{guardadoLastfm}".',
  'Trocar cookies': 'Swap cookies',
  'Crie uma conta em iptv.facilemacile.ltd, cadastre lá a sua lista de IPTV e gere uma chave de API. Colada aqui, ela vale para a casa toda: a TV ao vivo passa a abrir no celular, no relógio e no Mac sem ninguém aprovar código nenhum.':
    'Create an account at iptv.facilemacile.ltd, register your IPTV list there and generate an API key. Pasted here, it covers the whole household: live TV starts opening on the phone, the watch and the Mac without anyone approving a code.',
  'A chave vale para a casa inteira e é quem administra que a cola. Uma chave não dá para sugerir sem entregá-la junto, então esta é a única coisa aqui que passa só por quem lidera.':
    'The key covers the whole household and whoever runs it is the one who pastes it. A key cannot be suggested without handing it over, so this is the one thing here that goes only through whoever leads.',
  'colar outra chave, ou deixar vazio para apagar': 'paste another key, or leave empty to delete',
  'Apagar a chave': 'Delete the key',
  'Salvar chave': 'Save key',
  'conectada em {host}': 'connected at {host}',
  'a conta da casa, para os dois': 'the household account, for both of you',
  '1 item já riscado': '1 item already crossed off',
  '{riscados} itens já riscados': '{riscados} items already crossed off',
  'Riscar {text}': 'Cross off {text}',

  // -- O anime: explorar, a grade e a coleção.
  'Descobrir anime': 'Discover anime',
  'Milhares de títulos de anime': 'Thousands of anime titles',
  'Anime por temporada e ano': 'Anime by season and year',
  'Explorar anime': 'Explore anime',
  'Tirar a marca de {nome}': 'Unmark {nome}',
  'Marcar {nome}': 'Mark {nome}',
  'Abrir {nome}': 'Open {nome}',
  'Tirar a marca de {v}': 'Unmark {v}',
  'Marcar {v}': 'Mark {v}',
  'Abrir {v}': 'Open {v}',
  'Não deu para carregar {v}': 'Could not load {v}',
  '{titulo}, voltar ao episódio {episodio}': '{titulo}, back to episode {episodio}',
  '{todos} animes na coleção de vocês': '{todos} anime in your collection',
  'Tirar {v} da playlist': 'Remove {v} from the playlist',
  'Renomear {nome}': 'Rename {nome}',
  'Apagar {nome}': 'Delete {nome}',
  'Criar playlist': 'Create playlist',
  'Renomear playlist': 'Rename playlist',
  'NO AR HOJE': 'ON AIR TODAY',
  'EM ALTA': 'TRENDING',
  'Mostrar {v}': 'Show {v}',
  'Em alta': 'Trending',
  '{quantos} episódios': '{quantos} episodes',
  '{quantos} já vistos': '{quantos} already watched',
  'Voltar ao EP {episodio}': 'Back to EP {episodio}',
  'EP {n} de {total}': 'EP {n} of {total}',
  'Episódio {n}': 'Episode {n}',
  'Salvar {nome} na playlist': 'Save {nome} to the playlist',
  'Salvar em {nome}': 'Save to {nome}',

  // -- O caderno, o calendário e a agenda.
  alguém: 'someone',
  'ex.: {exemplo} prefere café puro': 'e.g.: {exemplo} takes coffee black',
  'Adicionar uma data (opcional)': 'Add a date (optional)',
  'Sem título': 'No title',
  'Todo ano': 'Every year',
  'Todo mês': 'Every month',
  'Apagar {titulo}': 'Delete {titulo}',
  '"{titulo}" sai do calendário para vocês dois.':
    '"{titulo}" leaves the calendar for both of you.',
  'data especial': 'special date',
  'datas especiais': 'special dates',
  'Dia {numero}': 'Day {numero}',
  'Toque em um dia para ver os detalhes': 'Tap a day to see the details',
  'Editar calendário': 'Edit calendar',
  'Assinatura externa': 'External subscription',
  'Calendário do app': 'App calendar',
  'Compartilhar {nome}': 'Share {nome}',
  'Compartilhado. A outra pessoa vê os eventos, nunca o link.':
    'Shared. The other person sees the events, never the link.',
  'Só você vê este.': 'Only you see this one.',
  'Nenhum evento neste calendário.': 'No events in this calendar.',
  'Os eventos deste ficam na tela do calendário.':
    'The events for this one live on the calendar screen.',
  'Novo link (deixe vazio para manter)': 'New link (leave empty to keep)',
  'Link .ics': '.ics link',
  'Novo link': 'New link',
  'Cor {hex}': 'Color {hex}',

  // -- A cozinha: o cardápio, as compras e a despensa.
  'nada marcado ainda': 'nothing planned yet',
  'Voltar para esta semana': 'Back to this week',
  'esta semana': 'this week',
  'toque para voltar a hoje': 'tap to go back to today',
  'Marcar {nome} de {nome2}': 'Plan {nome} for {nome2}',
  'Tirar {titulo}': 'Remove {titulo}',
  'O livro ainda está vazio.': 'The book is still empty.',
  'Escrita para {porcoes} porções': 'Written for {porcoes} servings',
  'semana de {faixaDaSemana}': 'week of {faixaDaSemana}',
  '{comprados} de {itens} no carrinho': '{comprados} of {itens} in the cart',
  'Tirar {nome} da lista': 'Remove {nome} from the list',
  'Tirar {v} da lista': 'Remove {v} from the list',
  'o que tem em casa': 'what is in the house',
  'Passou da validade': 'Past its date',
  'Use logo': 'Use soon',
  'Validade de {nome}': '{nome} expiry date',
  'Escolher a validade de {nome}': 'Choose the expiry date for {nome}',
  'Tirar {nome} da despensa': 'Remove {nome} from the pantry',
  'Tirar dos favoritos': 'Remove from favorites',
  Favoritar: 'Add to favorites',
  '"{titulo}" sai do livro para vocês dois.': '"{titulo}" leaves the book for both of you.',

  // -- As cartas, a galeria, os sonhos e as contas.
  'Fechar a busca': 'Close the search',
  'Nenhuma carta encontrada': 'No letters found',
  'Nenhuma carta ainda': 'No letters yet',
  'Nenhuma carta aqui': 'No letters here',
  'Volte mais tarde!': 'Come back later!',
  ' · realizado em {v}': ' · come true on {v}',
  'Apagar {v}': 'Delete {v}',
  desejo: 'wish',
  'Conta no Sicredi': 'Sicredi account',
  'Dados de teste': 'Test data',
  Lançamento: 'Entry',
  'Nada com essa palavra': 'Nothing with that word',
  'Nenhuma memória ainda': 'No memories yet',
  'Procure por outra legenda ou etiqueta.': 'Search for another caption or tag.',
  'Toque em + para colocar a primeira foto': 'Tap + to add the first photo',
  'Procurando adversário...': 'Looking for an opponent...',
  'Precisa de uma conta ligada à casa': 'Needs an account linked to the household',
  'Saia e entre de novo para renovar sua sessão.': 'Sign out and back in to renew your session.',
  'Cor {nome}': 'Color {nome}',
  '{nome} está desenhando': '{nome} is drawing',
  '{nome} está aqui': '{nome} is here',
  'rabiscando agora': 'doodling now',
  'sem rabisco no momento': 'no doodle right now',

  // -- O cinema: as abas, a sessão, o tocador e a IPTV.
  'Carregando…': 'Loading…',
  'Carregar mais': 'Load more',
  'A sessão está tocando outra coisa.': 'The session is playing something else.',
  'Sessão aberta agora.': 'Session open right now.',
  'Vejam ao mesmo tempo, cada um no seu sofá.': 'Watch at the same time, each on your own sofa.',
  'Procurando versões disponíveis...': 'Looking for available versions...',
  'Quanto mais fontes, mais estável a reprodução': 'The more sources, the steadier the playback',
  '{fontes} fontes': '{fontes} sources',
  'Nada nesta categoria.': 'Nothing in this category.',
  'Procurando em todo o catálogo...': 'Searching the whole catalog...',
  'Nada com esse nome, nem nas outras categorias.':
    'Nothing by that name, and nothing in the other categories either.',
  // A conta logo abaixo das três de cima. Ficou em português até agora porque
  // era um molde com `${}` no meio do JSX, e é justamente o que o varredor do
  // `frases.mjs` avisa no cabeçalho que não enxerga.
  '1 item': '1 item',
  '{n} itens': '{n} items',
  '1 resultado': '1 result',
  '{n} resultados': '{n} results',
  'Conecte a assinatura uma vez e ela fica disponível para vocês dois.':
    'Connect the subscription once and it stays available to both of you.',
  'Abra o link abaixo e aprove este código na sua conta.':
    'Open the link below and approve this code in your account.',
  'Aprove este código na sua conta do serviço.': 'Approve this code in your service account.',
  'Comece uma sessão ou volte de onde pararam.': 'Start a session or pick up where you left off.',
  'Disponível no perfil do casal.': 'Available on the couple profile.',
  'Prontos para o cinema': 'Ready for movie night',
  'Sessão pronta': 'Session ready',
  'Escolha um título abaixo ou apresente do Jellyfin.':
    'Pick a title below, or present from Jellyfin.',
  'Escolha um título abaixo para abrir o player sincronizado.':
    'Pick a title below to open the synced player.',
  Anfitrião: 'Host',
  Convidado: 'Guest',
  'Voltar para a sessão': 'Back to the session',
  'Entrar na sessão': 'Join the session',
  'Escreva para {parceiro}': 'Write to {parceiro}',
  'Escreva para {parceiro}...': 'Write to {parceiro}...',
  'Ligar o som': 'Turn the sound on',
  'Tirar o som': 'Mute',
  'Ver {titulo}': 'See {titulo}',
  'Trailer de {titulo}': '{titulo} trailer',
  'Fechar o formulário': 'Close the form',
  'Conectar servidor próprio': 'Connect your own server',
  'Tirar {nome} deste aparelho': 'Remove {nome} from this device',
  'Câmera ligada': 'Camera on',
  Câmera: 'Camera',
  'Sem câmera nesta chamada. A conversa segue só com voz.':
    'No camera on this call. The conversation goes on with voice only.',
  'Chamando {parceiro}': 'Calling {parceiro}',
  'Sem som': 'Muted',
  'Ao vivo': 'Live',
  Sessão: 'Session',
  'Temporada {season_number}': 'Season {season_number}',
  'Episódio {numero}': 'Episode {numero}',
  'PERSONAGENS E VOZES': 'CHARACTERS AND VOICES',
  ELENCO: 'CAST',
  Filme: 'Movie',
  'Reproduzindo direto do enxame': 'Playing straight from the swarm',

  // -- Os livros: a busca, o detalhe, o leitor e a estante.
  'Nenhum livro encontrado.': 'No books found.',
  'Comece a digitar para achar um livro...': 'Start typing to find a book...',
  'Ler menos': 'Read less',
  'Ler mais...': 'Read more...',
  'Nenhum arquivo para baixar neste título, nos acervos livres que usamos.':
    'No file to download for this title, in the free collections we use.',
  'Baixar {tipo}': 'Download {tipo}',
  'Prévia do Internet Archive': 'Internet Archive preview',
  'Pegar emprestado': 'Borrow',
  'Tela cheia': 'Full screen',
  'Abrir no {rotuloDaFonte}': 'Open in {rotuloDaFonte}',
  'Tente outro título, outro autor, ou troque o idioma aqui em cima.':
    'Try another title, another author, or change the language up above.',
  'Carregando os audiolivros mais ouvidos.': 'Loading the most-listened audiobooks.',
  'Formatos em domínio público, via {acervo}': 'Public-domain formats, via {acervo}',
  'NOSSA BIBLIOTECA': 'OUR LIBRARY',
  'A ESTANTE': 'THE SHELF',
  'Você ainda não terminou nenhum': 'You have not finished any yet',
  'Nada na fila ainda': 'Nothing in the queue yet',
  'Os livros que você marcar como lidos ficam aqui para reler quando quiser.':
    'The books you mark as read stay here to re-read whenever you like.',
  'Toque no marcador de qualquer livro para colocá-lo na fila de leitura.':
    'Tap the bookmark on any book to put it in the reading queue.',
  'PARA LER': 'TO READ',
  'Nenhuma escolha em comum ainda.\nBusque um livro e coloque em "Nossos".':
    'No shared pick yet.\nSearch for a book and put it in "Ours".',
  'Nada lido junto ainda.\nO primeiro livro de vocês espera.':
    'Nothing read together yet.\nYour first book is waiting.',

  // -- As duas formas que `contagem()` escolhe antes de traduzir.
  //
  // A escolha singular/plural é feita no código, então o dicionário guarda as
  // duas soltas. O número não entra no verbete: ele é escrito em volta.
  'prato marcado': 'dish planned',
  'pratos marcados': 'dishes planned',

  // -- A quinta passada: a frase que nunca esteve dentro do JSX.
  //
  // Três lugares, a mesma regra. O recado que uma função manda para a tela
  // (`setRecado('Salvo.')`), a frase escrita no meio de uma função e a palavra
  // que mora numa tabela do topo do arquivo. As duas primeiras são embrulhadas
  // onde estão; a terceira fica em português na constante e é embrulhada por
  // quem a lê, porque a constante é avaliada quando o módulo carrega -- antes
  // de o núcleo ligar -- e traduzir ali congelaria a primeira língua para
  // sempre.
  //
  // O que ficou de fora de propósito: a frase que já nasce em inglês (a volta
  // devolve a chave intacta, então verbete nenhum é preciso), o nome do app, o
  // nome de servidor e o texto que atravessa para o outro lado ou para o banco.

  // -- O saudar do painel e os cartões da tela inicial.
  'bom dia': 'good morning',
  'boa tarde': 'good afternoon',
  'boa noite': 'good evening',
  'bem-vindos ao nosso lar': 'welcome to our home',
  'Agora mesmo': 'Just now',
  'Sem clima agora': 'No mood right now',
  'a próxima data': 'the next date',
  'nada na prateleira': 'nothing on the shelf',
  'um livro': 'one book',

  // -- O tocador: o que o navegador não deixou acontecer.
  'A gravação não abriu.': 'The recording would not open.',
  'O navegador não deixou tocar. Toque de novo.': 'The browser would not let it play. Tap again.',
  'Não deu para voltar a tocar.': 'Could not resume playback.',

  // -- A academia: a espera da partida e o pódio.
  'Procurando uma partida...': 'Looking for a match...',
  'Esperando seu par...': 'Waiting for your partner...',
  'Ninguém entrou. Que tal estudar sozinho?': 'Nobody joined. How about studying solo?',
  'Não deu para começar: {v}': 'Could not start: {v}',
  'Separando as perguntas...': 'Picking the questions...',
  'Vitória!': 'Victory!',
  'Bom esforço!': 'Good effort!',
  'Vocês dois são incríveis!': 'You two are amazing!',
  'Você dominou a partida!': 'You owned the match!',
  'Mais sorte na próxima!': 'Better luck next time!',
  '{v} pontos de estudo': '{v} study points',

  // -- Os ajustes: o código, os avisos, a casa e as chaves de acesso.
  'Digite seu código atual, os quatro dígitos.': 'Enter your current code, all four digits.',
  'O novo código precisa ter 4 dígitos.': 'The new code needs 4 digits.',
  'Os códigos novos não bateram.': 'The new codes did not match.',
  'Esse já é o seu código.': 'That is already your code.',
  'Pronto. Seu novo código já está valendo.': 'Done. Your new code is already in effect.',
  'Não foi possível trocar o código.': 'Could not change the code.',
  'Código trocado. O antigo não vale mais.': 'Code changed. The old one no longer works.',
  'Pronto. Este aparelho vai avisar você.': 'Done. This device will notify you.',
  'Notificações desligadas neste aparelho.': 'Notifications turned off on this device.',
  'Enviamos um aviso de teste. Ele deve chegar em instantes.':
    'We sent a test notice. It should arrive in a moment.',
  'Não foi possível enviar o teste agora.': 'Could not send the test right now.',
  'Sugestão enviada.': 'Suggestion sent.',
  'Salvo.': 'Saved.',
  'Não foi possível salvar agora.': 'Could not save right now.',
  'Aceito. Já vale para todo mundo.': 'Accepted. It applies to everyone now.',
  'Recusado.': 'Declined.',
  'Não foi possível responder agora.': 'Could not answer right now.',
  'A data precisa estar como dia/mês/ano.': 'The date has to be day/month/year.',
  'Salvo. Já vale para todo mundo.': 'Saved. It applies to everyone now.',
  'Tudo voltou ao padrão.': 'Everything is back to default.',
  'Muitas tentativas. Aguarde um pouco antes de tentar de novo.':
    'Too many tries. Wait a little before trying again.',
  'Não foi possível concluir. Tente novamente.': 'Could not finish. Try again.',
  'ainda não usada': 'not used yet',
  'Digite seu código de 4 dígitos.': 'Enter your 4-digit code.',
  'Este dispositivo': 'This device',
  'Chave salva. Já dá para entrar com ela.': 'Key saved. You can sign in with it now.',
  'Novo nome': 'New name',
  'Chave removida.': 'Key removed.',
  'Minha chave de segurança': 'My security key',

  // -- As contas conectadas e a Alexa.
  'Desvinculado. Os links voltam a abrir na conta padrão.':
    'Unlinked. Links go back to opening in the default account.',
  'Guardado. Agora use o botão para deixar essa conta ativa.':
    'Saved. Now use the button to make that account the active one.',
  'Guardado.': 'Saved.',
  'Desvinculado. O painel para de mostrar o que você ouve.':
    'Unlinked. The dashboard stops showing what you listen to.',
  'Guardado. O painel já lê essa conta, e a casa toda vê.':
    'Saved. The dashboard reads that account now, and the whole house sees it.',
  'Conta trocada nessa aba. Os links de música já abrem nela.':
    'Account switched in this tab. Music links open in it now.',
  'Não consegui abrir o YouTube Music.': 'Could not open YouTube Music.',
  'ainda não ligada': 'not connected yet',
  'Não deu para ler as conexões agora.': 'Could not read the connections right now.',
  'O servidor não respondeu agora.': 'The server did not answer.',
  'Cole os cookies da sessão que já está aberta no navegador.':
    'Paste the cookies from the session already open in your browser.',
  'Conta ligada.': 'Account connected.',
  'Testada agora.': 'Tested just now.',
  '{nome} desconectada.': '{nome} disconnected.',
  Ligar: 'Connect',
  'A chave guardada foi recusada pelo serviço. Revogue-a por lá e cole uma nova.':
    'The saved key was rejected by the service. Revoke it there and paste a new one.',
  'Ligada pela chave da conta. Não vence.': 'Connected by the account key. It does not expire.',
  'Ligada pelo pareamento, que vence de tempos em tempos. Uma chave resolve isso de vez.':
    'Connected by pairing, which expires from time to time. A key settles it for good.',
  'Sem chave e sem pareamento: a TV ao vivo não abre.':
    'No key and no pairing: live TV will not open.',
  'Chave apagada. A TV ao vivo volta a depender do pareamento.':
    'Key deleted. Live TV goes back to depending on pairing.',
  'Chave guardada. A TV ao vivo já funciona em todos os apps.':
    'Key saved. Live TV works in every app now.',
  'A Alexa não respondeu agora.': 'Alexa did not answer.',
  'Cole os cookies da sessão da Alexa.': 'Paste the cookies from the Alexa session.',
  'Alexa conectada.': 'Alexa connected.',
  'Alexa desconectada.': 'Alexa disconnected.',
  '"{name}" agora aparece para vocês dois.': '"{name}" now shows up for both of you.',
  'Esse item veio de uma importação antiga. Atualize a lista.':
    'That item came from an old import. Refresh the list.',
  'Lista removida daqui.': 'List removed from here.',
  'Ainda não foi conferida desde que ligou.': 'Not checked since it was connected.',
  'Conferida agora há pouco.': 'Checked just now.',
  'conta da casa': 'house account',
  'Aperte F12 (no Mac, Cmd + Option + I) e vá na aba Rede.':
    'Press F12 (on Mac, Cmd + Option + I) and open the Network tab.',
  'Recarregue a página e clique em qualquer linha que aparecer.':
    'Reload the page and click any row that shows up.',
  'Em Cabeçalhos, ache "Cookie" e copie o valor inteiro.':
    'Under Headers, find "Cookie" and copy the whole value.',

  // -- O anime: a tela inicial, a coleção e a ficha do título.
  Explorar: 'Explore',
  'Em alta esta semana': 'Trending this week',
  'No ar em alta': 'Popular and airing',
  'Mais populares': 'Most popular',
  'Terminados há pouco': 'Recently finished',
  'Na coleção': 'In the collection',
  'Já vistos': 'Already watched',
  'Quero ver': 'Want to watch',
  'Nada para mostrar': 'Nothing to show',
  Estúdios: 'Studios',
  'Não informado': 'Not listed',
  Duração: 'Runtime',
  Gêneros: 'Genres',

  // -- O caderno, o calendário e a cozinha: o que não deu para gravar.
  'Não deu para mudar isso agora. Tente de novo.': 'Could not change that right now. Try again.',
  'Não deu para esquecer agora. Tente de novo.': 'Could not forget it right now. Try again.',
  'Não deu para guardar agora. Tente de novo.': 'Could not save it right now. Try again.',
  'Não deu para marcar o compromisso.': 'Could not save the appointment.',
  'Não deu para apagar o compromisso.': 'Could not delete the appointment.',
  'Escreva o que é.': 'Write what it is.',
  'Não deu certo agora. Tente de novo.': 'That did not work. Try again.',
  'Não deu para salvar o cardápio.': 'Could not save the menu.',
  '{titulo} saiu do cardápio.': '{titulo} left the menu.',
  'Não deu para salvar a lista.': 'Could not save the list.',
  'O cardápio dessa semana está vazio.': "That week's menu is empty.",
  'Lista refeita a partir do cardápio.': 'List rebuilt from the menu.',
  'Não deu para gerar a lista.': 'Could not build the list.',
  '{v} na despensa.': '{v} in the pantry.',
  'Não deu para guardar na despensa.': 'Could not put it in the pantry.',
  'Não deu para salvar a despensa.': 'Could not save the pantry.',
  '{nome} saiu da despensa.': '{nome} left the pantry.',
  'Não deu para salvar a receita.': 'Could not save the recipe.',
  'Não deu para apagar a receita.': 'Could not delete the recipe.',
  'Receita salva.': 'Recipe saved.',
  'Cole o endereço da receita.': "Paste the recipe's address.",
  'Não deu para alcançar essa página.': 'Could not reach that page.',
  'A foto tem de ter até 8 MB.': 'The photo has to be 8 MB or smaller.',
  'Não deu para guardar a foto: {message}': 'Could not save the photo: {message}',
  'A receita precisa de um nome.': 'The recipe needs a name.',

  // -- O cinema: baixar, a sessão a dois, a ficha do título e a TV ao vivo.
  'Nenhum filme encontrado. Tenta outro título.': 'No movie found. Try another title.',
  'Nenhum vídeo para baixar neste item.': 'No video to download in this item.',
  'Não deu para baixar {nome}': 'Could not download {nome}',
  'Escolha o que assistir antes de começar a sessão.':
    'Pick what to watch before starting the session.',
  'Não deu para abrir a sessão agora. Tente de novo em alguns minutos.':
    'Could not open the session right now. Try again in a few minutes.',
  'Trocar para este': 'Switch to this one',
  'Voltar à sessão': 'Back to the session',
  'Miau... não achei nada agora! Tenta de novo daqui a pouco?':
    'Meow... I found nothing right now! Try again in a bit?',
  'A conta conectada não tem nenhuma lista.': 'The connected account has no lists.',
  'O serviço de TV não respondeu. Tente de novo em instantes.':
    'The TV service did not answer. Try again in a moment.',
  'Não foi possível pedir um código agora.': 'Could not ask for a code right now.',
  'Suas listas': 'Your lists',
  Categorias: 'Categories',
  'Este canal não está disponível agora.': 'This channel is not available right now.',
  'Esta série não listou episódios.': 'This series listed no episodes.',
  'Sessão aberta. É só o outro lado entrar.': 'Session open. The other side just has to join.',
  'Não deu para abrir a sessão agora.': 'Could not open the session right now.',
  'Não deu para abrir a sessão de {nome}': 'Could not open the session for {nome}',
  'Nenhum filme na biblioteca para "{t}".': 'No movie in the library for "{t}".',
  'Não deu para enviar a mensagem.': 'Could not send the message.',
  'Não deu para limpar o chat.': 'Could not clear the chat.',
  'Não deu para enviar: {e}': 'Could not send: {e}',
  'Toque para ver detalhes, episódios, elenco e mais.': 'Tap for details, episodes, cast and more.',
  'Nome, host e URL do stream são obrigatórios.': 'Name, host and stream URL are required.',
  'Coloque uma URL http(s) válida.': 'Enter a valid http(s) URL.',
  'Não deu para salvar o servidor.': 'Could not save the server.',
  '{parceiroNome} encerrou a sessão.': '{parceiroNome} ended the session.',
  '{parceiroNome} começou esta sessão e comanda o filme.':
    '{parceiroNome} started this session and controls the movie.',
  'Você pausou': 'You paused',
  'Toque para entrar': 'Tap to join',
  'Entre para mexer na lista de interesses': 'Sign in to change the watch list',
  'Tirado da lista': 'Removed from the list',
  'Não deu para remover. Tente de novo.': 'Could not remove it. Try again.',
  'Lista atualizada': 'List updated',
  'Não deu para atualizar. Tente de novo.': 'Could not update it. Try again.',
  'Todo mundo vendo': 'Everyone watching',
  'Todo mundo viu': 'Everyone watched',
  '{nome} definido como padrão': '{nome} set as the default',
  'Este título não tem correspondência no IMDb.': 'This title has no match on IMDb.',
  'Não foi possível preparar este torrent agora.': 'Could not prepare this torrent right now.',
  'Não deu para iniciar o vídeo.': 'Could not start the video.',
  'O tocador de vídeo não carregou.': 'The video player did not load.',
  'Não deu para carregar o vídeo.': 'Could not load the video.',
  'Não deu para falar com o servidor agora.': 'Could not reach the server right now.',
  'O Sicredi não respondeu agora.': 'Sicredi did not answer.',
  'O Kovu não disse nada. Tente de novo.': 'Kovu said nothing. Try again.',

  // -- Os livros: a busca, a ficha, o leitor e o audiolivro.
  '{titulo} entrou nos Nossos Livros!': '{titulo} joined Our Books!',
  '{titulo} entrou no Nosso Sempre!': '{titulo} joined Nosso Sempre!',
  'Entre para salvar livros': 'Sign in to save books',
  'Entre na conta para salvar livros': 'Sign in to save books',
  'Tirado da sua lista': 'Removed from your list',
  'Salvo na sua lista de leitura': 'Saved to your reading list',
  'Este título é de empréstimo no Internet Archive: não há arquivo para baixar. Toque em Ler para ver a prévia e pegar emprestado.':
    'This title is lending-only on the Internet Archive: there is no file to download. Tap Read to see the preview and borrow it.',
  'Link copiado. Mande para quem você ama': 'Link copied. Send it to the one you love',
  'Link copiado': 'Link copied',
  'Nenhuma cópia legível deste título.': 'No readable copy of this title.',
  'Não deu para carregar o texto do livro em nenhuma fonte disponível.':
    "Could not load the book's text from any available source.",
  'Erro: {v}': 'Error: {v}',
  'Este título não tem texto para ouvir.': 'This title has no text to listen to.',
  'Ouvir precisa de um navegador com síntese de voz.':
    'Listening needs a browser with speech synthesis.',
  'Não consegui carregar as faixas deste audiolivro agora.':
    "Could not load this audiobook's tracks right now.",
  'Não consegui abrir o áudio deste audiolivro.': "Could not open this audiobook's audio.",

  // -- O refúgio: o resultado do teste de conexão, que vem em parágrafos.
  '[OK] Deu certo!\n\nEste aparelho alcança o servidor. Se ainda assim as mensagens não aparecem, confira se a conexão do outro lado também está boa.':
    '[OK] It worked!\n\nThis device reaches the server. If the messages still do not show up, check that the connection on the other side is good too.',
  '[FALHOU] Não deu.\n\nErro: {v}\n\nGeralmente é a sessão que venceu: saia e entre de novo.':
    '[FAILED] It did not work.\n\nError: {v}\n\nUsually it is the session that expired: sign out and back in.',

  // -- As tabelas do topo de arquivo: a palavra fica em português na constante
  // e é traduzida por quem a lê. Ver o cabeçalho da quinta passada, acima.

  // As categorias do sonho (`BucketCategory`). `Viagem`, `Aventura` e `Outros`
  // já têm verbete lá em cima, no cinema e nos modos de ver.
  Experiência: 'Experience',
  'Comida e bebida': 'Food and drink',
  Conquista: 'Milestone',

  // -- As abas, os atalhos e os modos de ver.
  'A semana': 'The week',
  Cardápio: 'Menu',
  Compras: 'Shopping',
  Cozinha: 'Kitchen',
  Receitas: 'Recipes',
  Cartas: 'Letters',
  Bilhetes: 'Notes',
  Galeria: 'Gallery',
  Quadro: 'Canvas',
  Memórias: 'Memories',
  Sonhos: 'Dreams',
  'Lista de sonhos': 'Bucket list',
  Datas: 'Dates',
  Data: 'Date',
  Filmes: 'Movies',
  Séries: 'Series',
  'TV ao Vivo': 'Live TV',
  Juntos: 'Together',
  Desenhar: 'Draw',
  Grade: 'Grid',
  Lista: 'List',
  Listas: 'Lists',
  Fila: 'Queue',
  Geral: 'General',
  Outros: 'Other',
  Novidades: "What's new",
  Novos: 'New',
  'Mais recentes': 'Newest',
  Populares: 'Popular',
  Favoritos: 'Favorites',
  'Em casa': 'At home',
  'No mercado': 'At the market',
  'Sem hierarquia': 'No hierarchy',
  Poliamorosa: 'Polyamorous',
  'Seu gato': 'Your cat',

  // -- Os estados: da carta, do anime, do sonho, do hábito e da meta.
  Aberta: 'Open',
  Fechada: 'Closed',
  Lacradas: 'Sealed',
  Lidas: 'Read',
  'Não lidas': 'Unread',
  Todas: 'All',
  Todos: 'All',
  Assistindo: 'Watching',
  Terminados: 'Finished',
  Planejado: 'Planned',
  'Para ver': 'To watch',
  Visto: 'Watched',
  Vistos: 'Watched',
  Feito: 'Done',
  Desejo: 'Wish',
  Realizado: 'Completed',
  Hábito: 'Habit',
  Meta: 'Goal',
  Fato: 'Fact',
  Gratidão: 'Gratitude',
  'Gosta de': 'Likes',
  'Não gosta': 'Does not like',

  // -- Os corredores do mercado e as refeições do dia.
  Hortifrúti: 'Produce',
  'Açougue e peixaria': 'Butcher and fish',
  'Laticínios e ovos': 'Dairy and eggs',
  Padaria: 'Bakery',
  Mercearia: 'Grocery',
  Congelados: 'Frozen',
  Bebidas: 'Drinks',
  Limpeza: 'Cleaning',
  Higiene: 'Toiletries',
  'Café da manhã': 'Breakfast',
  Almoço: 'Lunch',
  Jantar: 'Dinner',
  Lanche: 'Snack',

  // -- Os gêneros e as descobertas do cinema.
  Ação: 'Action',
  Aventura: 'Adventure',
  Comédia: 'Comedy',
  Terror: 'Horror',
  Suspense: 'Thriller',
  Mistério: 'Mystery',
  Fantasia: 'Fantasy',
  'Ficção científica': 'Science fiction',
  Sobrenatural: 'Supernatural',
  Desenhos: 'Animation',
  Esportes: 'Sports',
  Viagem: 'Travel',
  Ciência: 'Science',
  Famosos: 'Celebrities',
  Clássicos: 'Classics',
  'O melhor dos anos 2000': 'Best of the 2000s',
  'O melhor dos anos 2010': 'Best of the 2010s',
  'Cinema francês': 'French cinema',
  'Cinema espanhol': 'Spanish cinema',
  'Doramas coreanos': 'Korean dramas',
  Português: 'Portuguese',
  Inglês: 'English',
  Espanhol: 'Spanish',

  // -- As ordens da estante e a paleta do quadro.
  'Ordem: mais relevantes': 'Order: most relevant',
  'Ordem: mais populares': 'Order: most popular',
  'Ordem: mais novos': 'Order: newest',
  'Ordem: mais antigos': 'Order: oldest',
  'Ordem: maiores': 'Order: longest',
  'Ordem: menores': 'Order: shortest',
  'Ordem: aleatória': 'Order: random',
  'Amarelo suave': 'Soft yellow',
  'Azul claro': 'Light blue',
  'Verde claro': 'Light green',
  Lavanda: 'Lavender',
  Rosa: 'Pink',

  // -- A sexta passada: a palavra que só chega à tela por um funil.
  //
  // O que falta aqui não é uma frase escondida no JSX; é uma tabela do topo do
  // arquivo que ninguém lê direto. Quem a lê é uma função -- `nomeDaRefeicao`,
  // `nomeDoCorredor`, `rotuloDeStatus` -- ou um componente que recebe a tabela
  // inteira por prop, como as `Marcas` do celular. A volta foi para dentro do
  // funil, onde o corpo da função roda na hora de desenhar, e a tabela
  // continua em português, que é o que o módulo carrega.
  //
  // O nome de gente nunca passa por funil nenhum: ele entra como buraco.

  // A relação, a distância e o momento (`types/casa.ts`).
  'Dividindo a mesma casa': 'Sharing the same home',
  'Em casas separadas': 'In separate homes',
  'Em fusos diferentes': 'In different time zones',
  Começando: 'Just starting',
  Namorando: 'Dating',
  Noivos: 'Engaged',
  Casados: 'Married',
  'Juntos há anos': 'Together for years',

  // As categorias do pote de estrelas. A `chave` fica crua: é ela que é gravada.
  Memória: 'Memory',
  Amor: 'Love',
  Sonho: 'Dream',
  Marco: 'Milestone',
  Surpresa: 'Surprise',

  // Os temas e o que cada um é.
  'Rosa Crepúsculo': 'Twilight Rose',
  'Lilás Noturno': 'Nocturnal Lilac',
  'Azul de Meia-noite': 'Midnight Blue',
  'Verde Musgo': 'Moss Green',
  'Âmbar de Vela': 'Candle Amber',
  'Coral de Verão': 'Summer Coral',
  'o original, rosa sobre violeta': 'the original, rose over violet',
  'violeta mais fundo, quase uva': 'deeper violet, almost grape',
  'frio e calmo, azul sobre índigo': 'cool and calm, blue over indigo',
  'esmeralda sobre mata fechada': 'emerald over deep forest',
  'dourado quente, luz de jantar': 'warm gold, dinner light',
  'laranja vivo sobre terracota': 'bright orange over terracotta',
  'já é de {quem}': 'already belongs to {quem}',

  // Quem quer ver, quem já viu e quem já leu. O nome sai da casa e entra como
  // buraco, nunca como verbete.
  Meu: 'Mine',
  'Todo mundo': 'Everyone',
  '{quem} viu': '{quem} watched it',
  '{quem} assistindo': '{quem} is watching',
  'Todo mundo leu': 'Everyone read it',
  '{quem} leu': '{quem} read it',

  // O que o servidor consertou no caminho, na seção da casa.
  'com {quantas} pessoas a relação não cabe em "{pedida}", então ficou "{ficou}"':
    'with {quantas} people the relationship does not fit in "{pedida}", so it became "{ficou}"',
  'não conheço o fuso "{pedido}", então {quem} ficou em {fuso}':
    'I do not know the time zone "{pedido}", so {quem} was set to {fuso}',
  'Salvo, com um ajuste: {avisos}.': 'Saved, with one adjustment: {avisos}.',

  // A palavra solta que a terceira passada deixou passar: o filtro de lá era
  // "tem espaço ou tem acento", e estas duas não têm nem um nem outro.
  Humores: 'Moods',
  Relacionados: 'Related',

  // ---------------------------------------------------------------------------
  // As frases que vêm do servidor.
  //
  // O servidor fala português na linha: as mensagens dele estão escritas lá em
  // português, e mudar isso pediria um protocolo novo só para texto de erro --
  // versionado, e errado no dia em que o app for mais novo que a máquina. Em vez
  // disso elas entram aqui como qualquer outro verbete, e quem traduz é o
  // cliente, no instante em que a frase chega.
  //
  // Cuidar disto é cuidar de dois arquivos: `server/src/index.js` (o `MSG`) e
  // `server/src/tools.js` (os `status:`). Uma frase mudada lá e esquecida aqui
  // não quebra nada -- ela só volta a sair em português, que é o original.
  // ---------------------------------------------------------------------------

  // O que o Kovu está fazendo enquanto a ferramenta roda. É a única parte da
  // conversa que não passa pelo modelo, então é a única que precisa de verbete.
  'Conferindo o app…': 'Checking the app…',
  'Ajustando o app…': 'Adjusting the app…',
  'Olhando a agenda…': 'Checking the calendar…',
  'Lendo as listas…': 'Reading the lists…',
  'Mandando para a Alexa…': 'Sending to Alexa…',
  'Olhando a cozinha…': 'Looking in the kitchen…',
  'Conferindo as conexões…': 'Checking the connections…',
  'Mandando o recado…': 'Sending the message…',
  'Guardando isso…': 'Saving that…',
  'Procurando no catálogo…': 'Searching the catalog…',
  'Procurando na estante…': 'Searching the shelf…',
  'Procurando na TV ao vivo…': 'Searching live TV…',
  'Procurando uma cópia…': 'Looking for a copy…',
  'Olhando o app…': 'Looking in the app…',
  'Guardando no app…': 'Saving to the app…',
  'Procurando na internet…': 'Searching the web…',
  'Lendo a página…': 'Reading the page…',
  'Perguntando ao Google…': 'Asking Google…',
  'Lendo o link…': 'Reading the link…',
  'Fazendo a conta…': 'Doing the math…',
  'Olhando o arquivo…': 'Looking at the file…',
  'Vendo o vídeo…': 'Watching the video…',
  'Desenhando…': 'Drawing…',
  'Gravando a voz…': 'Recording the voice…',
  'Procurando pelo sentido…': 'Searching by meaning…',
  'Trabalhando nisso…': 'Working on it…',

  // O Kovu quando não dá. Duas destas nascem no servidor e duas no cliente, e
  // daqui não dá para saber qual é qual -- nem precisa.
  'O Kovu está fora do ar no momento.': 'Kovu is offline right now.',
  'O Kovu não conseguiu responder agora. Tente de novo.':
    'Kovu could not answer right now. Try again.',
  'O Kovu se perdeu no meio da resposta. Tente de novo.':
    'Kovu lost track in the middle of the answer. Try again.',
  'O Kovu não respondeu.': 'Kovu did not answer.',
  'O Kovu falhou.': 'Kovu failed.',

  // A porta de entrada. São as frases que mais aparecem para quem erra o código,
  // e eram as últimas em português numa tela inteira em inglês.
  'Sua sessão expirou. Entre novamente.': 'Your session expired. Sign in again.',
  'O código precisa ter 4 dígitos.': 'The code must be 4 digits.',
  'Perfil desconhecido.': 'Unknown profile.',
  'Esse perfil já tem um código definido.': 'That profile already has a code.',
  'Escolha um código diferente do da outra pessoa.':
    "Choose a code different from the other person's.",
  'Código incorreto.': 'Wrong code.',
  'Esse perfil ainda não tem um código.': 'That profile does not have a code yet.',
  'O código da outra pessoa não confere.': "The other person's code does not match.",
  'Diga de quem é o código que você vai usar para confirmar.':
    'Say whose code you will use to confirm.',
  'O código novo é igual ao atual.': 'The new code is the same as the current one.',
  'Essa chave não foi encontrada.': 'That passkey was not found.',

  // O resto do `MSG`: o que o servidor responde quando o pedido em si não cabe.
  'Notificações ainda não estão configuradas no servidor.':
    'Notifications are not set up on the server yet.',
  'Inscrição de notificação inválida.': 'Invalid notification subscription.',
  'Tipo de notificação desconhecido.': 'Unknown notification type.',
  'O app ainda não está conectado ao banco de dados.':
    'The app is not connected to the database yet.',
  'A TV ao vivo ainda não está conectada. Conecte na tela de IPTV.':
    'Live TV is not connected yet. Connect it on the IPTV screen.',
  'Só quem administra a casa pode mudar isso.': 'Only whoever runs the house can change this.',
  'Enviado para quem administra a casa. Assim que responderem, muda para todo mundo.':
    'Sent to whoever runs the house. As soon as they answer, it changes for everyone.',

  // A prateleira do cinema. O original tem os dois lados escritos -- o padrão
  // de `ai_recommendations.dart` em português e o das chamadas em inglês --, e
  // aqui os dois viram um verbete só.
  'Escolhas do Kovu': "Kovu's Picks",

  // Quando o pedido nem chega ao servidor. Estas nascem no `client.ts`.
  'Erro {status}.': 'Error {status}.',
  'O servidor demorou demais para responder.': 'The server took too long to answer.',
  'Não foi possível falar com o servidor.': 'Could not reach the server.',
  'Não foi possível salvar.': 'Could not save.',

  // O sorteio de encontros manda a própria persona, e uma persona própria
  // substitui a do Kovu -- inclusive a linha que diz em que língua responder.
  // Por isso o pedido inteiro é verbete: o modelo precisa recebê-lo coerente.
  'Você é uma planejadora criativa de encontros. Crie títulos de encontros românticos e originais em português do Brasil. Responda APENAS com o título — sem aspas, sem markdown, sem mais nada.':
    'You are a creative date planner. Come up with original, romantic date titles in American English. Answer with the title ONLY — no quotes, no markdown, nothing else.',
  'Crie UM título de ideia de encontro romântico e original (curto, de 3 a 7 palavras). Seja criativa e pessoal.':
    'Come up with ONE original, romantic date idea title (short, 3 to 7 words). Be creative and personal.',
  'Humor de agora: {v}': 'Mood right now: {v}',
  'Quando: {v}': 'When: {v}',
  'Eles curtem: {v}': 'They like: {v}',
  'Responda APENAS com o título, em português do Brasil — sem aspas, sem explicação, sem mais nada.':
    'Answer with the title ONLY, in American English — no quotes, no explanation, nothing else.',
};
